import { MantineProvider, createTheme, ActionIcon, Button, NavLink, TextInput, NumberInput, Text, Loader, Paper, Switch, Modal, Textarea, Tooltip, Checkbox } from "@mantine/core";
import { useState, useRef, useCallback, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import Papa from "papaparse";

// ─── Donut chart constants ───────────────────────────────────────────────────
const GAP_DEGREES = 3;
const BASE_INNER  = 152;
const OUTER_R     = 175;
const LABEL_R     = 208;
const HANDLE_R    = 178;
const CX          = 210;
const CY          = 210;
const MIN_PCT     = 0.1;
const MAX_HISTORY = 20;
const INSIGHT_LIMIT = 3;
const REBAL_LIMIT   = 3;
const SNAP_LIMIT    = 10;
const TX_LIMIT      = 10;
const MATERIALITY_THRESHOLD = 3;

// ─── Track page sample data (render-only — never written to state or synced) ─
// Shown only for the seeded "Example account" (id 1) while it has no real
// snapshots, so first-time users see what a populated chart/ledger looks like.
const SAMPLE_TRACK_ACCOUNT_ID = 1;
const SAMPLE_TRACK_TXS = [
  { id: -1, accountId: SAMPLE_TRACK_ACCOUNT_ID, type: "deposit", amount: 1000, date: "2025-06-01" },
];
const SAMPLE_TRACK_SNAPS = [
  { id: -2, accountId: SAMPLE_TRACK_ACCOUNT_ID, value: 1000, date: "2025-06-01" },
  { id: -3, accountId: SAMPLE_TRACK_ACCOUNT_ID, value: 1009, date: "2025-07-01" },
  { id: -4, accountId: SAMPLE_TRACK_ACCOUNT_ID, value: 1100, date: "2025-08-01" },
  { id: -5, accountId: SAMPLE_TRACK_ACCOUNT_ID, value: 999,  date: "2025-09-01" },
  { id: -6, accountId: SAMPLE_TRACK_ACCOUNT_ID, value: 1250, date: "2025-10-01" },
  { id: -7, accountId: SAMPLE_TRACK_ACCOUNT_ID, value: 1300, date: "2025-11-01" },
  { id: -8, accountId: SAMPLE_TRACK_ACCOUNT_ID, value: 1350, date: "2025-12-01" },
];

// ─── Responsive breakpoints ──────────────────────────────────────────────────
const BP_MOBILE = 640;

// ─── Rebalance chart constants ───────────────────────────────────────────────
const REBAL_CX         = 210;
const REBAL_CY         = 210;
const REBAL_INNER      = 152;
const REBAL_OUTER_BASE = 175;
const REBAL_LABEL_R    = 208;
const REBAL_INPUT_R    = 268;
const REBAL_MAX_OUTER  = 4800;
const REBAL_MIN_OUTER  = 152;

// ─── Geometry helpers ────────────────────────────────────────────────────────
function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function arcPath(cx, cy, innerR, outerR, startDeg, endDeg) {
  const s1 = polarToCartesian(cx, cy, outerR, startDeg);
  const e1 = polarToCartesian(cx, cy, outerR, endDeg);
  const s2 = polarToCartesian(cx, cy, innerR, endDeg);
  const e2 = polarToCartesian(cx, cy, innerR, startDeg);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return [
    `M ${s1.x} ${s1.y}`,
    `A ${outerR} ${outerR} 0 ${large} 1 ${e1.x} ${e1.y}`,
    `L ${s2.x} ${s2.y}`,
    `A ${innerR} ${innerR} 0 ${large} 0 ${e2.x} ${e2.y}`,
    "Z",
  ].join(" ");
}

function mouseToAngle(e, svg) {
  const rect   = svg.getBoundingClientRect();
  const scaleX = 420 / rect.width;
  const scaleY = 420 / rect.height;
  const x      = (e.clientX - rect.left) * scaleX - CX;
  const y      = (e.clientY - rect.top)  * scaleY - CY;
  let angle    = (Math.atan2(y, x) * 180) / Math.PI + 90;
  if (angle < 0) angle += 360;
  return angle;
}

function touchToAngle(e, svg) {
  const touch  = e.touches[0] || e.changedTouches[0];
  const rect   = svg.getBoundingClientRect();
  const scaleX = 420 / rect.width;
  const scaleY = 420 / rect.height;
  const x      = (touch.clientX - rect.left) * scaleX - CX;
  const y      = (touch.clientY - rect.top)  * scaleY - CY;
  let angle    = (Math.atan2(y, x) * 180) / Math.PI + 90;
  if (angle < 0) angle += 360;
  return angle;
}

function computeSegments(boundaries) {
  const n = boundaries.length;
  return boundaries.map((b, i) => {
    const nextB  = boundaries[(i + 1) % n];
    const start  = (b + GAP_DEGREES / 2 + 360) % 360;
    let   end    = (nextB - GAP_DEGREES / 2 + 360) % 360;
    if (end <= start) end += 360;
    const mid    = (start + end) / 2;
    const sweep  = end - start;
    return { i, start, end, mid, sweep };
  });
}

function sweepToPct(segments) {
  const total = segments.reduce((s, seg) => s + seg.sweep, 0);
  return segments.map((seg) => Math.round((seg.sweep / total) * 1000) / 10);
}


function initBoundaries(n) {
  const step = 360 / n;
  return Array.from({ length: n }, (_, i) => (i * step) % 360);
}

function recalcBoundaries(currentBoundaries, pcts, n) {
  const totalSweep = 360 - n * GAP_DEGREES;
  const b0 = currentBoundaries[0];
  return currentBoundaries.map((_, i) => {
    if (i === 0) return b0;
    let cumSweep = 0;
    for (let j = 0; j < i; j++) cumSweep += (pcts[j] / 100) * totalSweep;
    return (b0 + cumSweep + i * GAP_DEGREES) % 360;
  });
}

// ─── CSV portfolio import ────────────────────────────────────────────────────
const MAX_IMPORT_SEGMENTS = 12;
const CSV_NAME_HEADERS  = ["name", "ticker", "symbol", "security", "description", "investment", "asset", "holding", "fund"];
const CSV_PCT_HEADERS   = ["allocation %", "allocation", "weight %", "weight", "% of portfolio", "portfolio %", "percentage", "percent", "%"];
const CSV_VALUE_HEADERS = ["market value", "current value", "total value", "value", "amount", "balance", "cost basis"];

function matchCsvHeader(headers, candidates) {
  const normalised = headers.map((h) => h.trim().toLowerCase());
  for (const candidate of candidates) {
    const idx = normalised.findIndex((h) => h === candidate);
    if (idx !== -1) return headers[idx];
  }
  for (const candidate of candidates) {
    const idx = normalised.findIndex((h) => h.includes(candidate));
    if (idx !== -1) return headers[idx];
  }
  return null;
}

function parseCsvNumber(raw) {
  if (raw == null) return NaN;
  const cleaned = String(raw).replace(/[^0-9.-]/g, "");
  return cleaned === "" || cleaned === "-" ? NaN : parseFloat(cleaned);
}

// Maps parsed CSV rows to [{ name, pct }] using whichever recognisable
// name + allocation/value columns are present. Returns null if the file
// doesn't look like a portfolio export we can interpret.
function csvRowsToHoldings(rows, headers) {
  const nameCol = matchCsvHeader(headers, CSV_NAME_HEADERS);
  const pctCol  = matchCsvHeader(headers, CSV_PCT_HEADERS);
  const valCol  = matchCsvHeader(headers, CSV_VALUE_HEADERS);
  const weightCol = pctCol || valCol;
  if (!nameCol || !weightCol) return null;

  const holdings = rows
    .map((r) => ({ name: String(r[nameCol] ?? "").trim(), weight: parseCsvNumber(r[weightCol]) }))
    .filter((h) => h.name && Number.isFinite(h.weight) && h.weight > 0);
  if (holdings.length < 2) return null;

  const total = holdings.reduce((s, h) => s + h.weight, 0);
  return holdings.map((h) => ({ name: h.name, pct: (h.weight / total) * 100 }));
}

// Caps the number of segments for chart readability — smallest holdings
// beyond the cap are folded into a single "Other" slice.
function consolidateHoldings(holdings, max) {
  if (holdings.length <= max) return holdings;
  const sorted = [...holdings].sort((a, b) => b.pct - a.pct);
  const kept   = sorted.slice(0, max - 1);
  const rest   = sorted.slice(max - 1);
  const otherPct = rest.reduce((s, h) => s + h.pct, 0);
  return [...kept, { name: "Other", pct: otherPct }];
}

// Converts [{ name, pct }] into the chart's native { boundaries, labels, n } shape.
function holdingsToDesign(holdings) {
  const newN       = holdings.length;
  const clamped    = holdings.map((h) => Math.max(MIN_PCT, h.pct));
  const total      = clamped.reduce((s, p) => s + p, 0);
  const normalised = clamped.map((p) => (p * 100) / total);
  return {
    boundaries: recalcBoundaries(initBoundaries(newN), normalised, newN),
    labels: holdings.map((h) => h.name),
    n: newN,
  };
}

function useWindowWidth() {
  const [w, setW] = useState(() => window.innerWidth);
  useEffect(() => {
    let timer;
    const handle = () => { clearTimeout(timer); timer = setTimeout(() => setW(window.innerWidth), 80); };
    window.addEventListener("resize", handle);
    return () => { window.removeEventListener("resize", handle); clearTimeout(timer); };
  }, []);
  return w;
}

function AppTooltip({ label, enabled, children, ...props }) {
  if (!enabled) return children;
  return <Tooltip label={label} withArrow multiline w={220} {...props}>{children}</Tooltip>;
}

function InvestmentAmountInput({ currency, setCurrency, portfolioSize, setPortfolioSize, tooltipsEnabled, isMobile }) {
  return (
    <>
      <Text c="#444" style={{ fontSize: isMobile ? 20 : 28, fontWeight: 600, letterSpacing: "0.06em", marginBottom: 16, lineHeight: 1.2 }}>
        Total portfolio investment
      </Text>
      <AppTooltip label="Enter the total amount you're planning to invest across all of your portfolio – the chart will auto-calculate percentage & absolute values of each component" enabled={tooltipsEnabled} position="right">
      <div style={{ display: "flex", border: "0.5px solid rgba(0,0,0,0.15)", borderRadius: 8, overflow: "hidden" }}>
        <select
          value={currency}
          onChange={(e) => setCurrency(e.target.value)}
          style={{
            width: 58, height: 36, flexShrink: 0,
            border: "none", borderRight: "0.5px solid rgba(0,0,0,0.12)",
            background: "#f8f8f8", fontSize: 14, color: "#555",
            paddingLeft: 12, paddingRight: 24, cursor: "pointer",
            outline: "none", appearance: "none", WebkitAppearance: "none",
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24'%3E%3Cpath fill='none' stroke='%23888' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round' d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
            backgroundRepeat: "no-repeat", backgroundPosition: "right 8px center", backgroundSize: "12px 12px",
          }}
        >
          <option>£</option>
          <option>$</option>
          <option>€</option>
        </select>
        <NumberInput
          hideControls
          value={portfolioSize}
          onChange={setPortfolioSize}
          placeholder="0"
          min={0}
          style={{ width: 100, flexShrink: 0 }}
          styles={{ input: { border: "none", borderRadius: 0, fontSize: 14, color: "#333", background: "transparent", height: 36, minHeight: "unset" } }}
        />
      </div>
      </AppTooltip>
    </>
  );
}

function loadDesignFromStorage() {
  try {
    const raw = localStorage.getItem("portfolioDesign");
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (!d || !Array.isArray(d.boundaries) || !Array.isArray(d.labels) || !d.n) return null;
    if (d.n < 2 || d.boundaries.length !== d.n || d.labels.length !== d.n) return null;
    return d;
  } catch { return null; }
}

// ─── Nav items ───────────────────────────────────────────────────────────────
const NAV_ITEMS = [
  { key: "design",    label: "Design",    icon: "ti-chart-donut",     tooltip: "Create & test an unlimited number of test portfolios, and build confidence – before you invest" },
  { key: "rebalance", label: "Rebalance", icon: "ti-arrows-exchange", tooltip: "Update the current value of your investments, and understand how to rebalance your portfolio – whenever you like" },
  { key: "track",     label: "Track",     icon: "ti-chart-line",      tooltip: "Periodically capture your transactions & p/l across all of your accounts to see your total long term performance – including when you take and spend some profits" },
];

// ─── App ─────────────────────────────────────────────────────────────────────
export default function App() {
  const windowWidth = useWindowWidth();
  const isMobile    = windowWidth < BP_MOBILE;
  const svgSize     = isMobile ? Math.min(420, windowWidth - 64) : 420;

  const [activePage, setActivePageRaw] = useState("design");
  const setActivePage = (page) => {
    setActivePageRaw(page);
    window.gtag?.('event', 'app_tab_view', { page, logged_in: !!session });
  };

  const [n, setN]                           = useState(() => { const d = loadDesignFromStorage(); return d ? d.n : 3; });
  const [boundaries, setBoundaries]         = useState(() => { const d = loadDesignFromStorage(); return d ? d.boundaries : initBoundaries(3); });
  const [hovered, setHovered]               = useState(null);
  const [draggingHandle, setDraggingHandle] = useState(null);
  const [editingIndex, setEditingIndex]     = useState(null);
  const [editValue, setEditValue]           = useState("");
  const [segmentLabels, setSegmentLabels]   = useState(() => { const d = loadDesignFromStorage(); return d ? d.labels : ["Enter investment name / ticker", "Enter investment name / ticker", "Enter investment name / ticker"]; });
  const [editingLabelIndex, setEditingLabelIndex] = useState(null);
  const [editLabelValue, setEditLabelValue] = useState("");
  const [hoveredLabel, setHoveredLabel]     = useState(null);
  const [portfolioName, setPortfolioName]   = useState(() => { const d = loadDesignFromStorage(); return d ? (d.name || "") : ""; });
  const [portfolioSize, setPortfolioSize]   = useState(() => { const d = loadDesignFromStorage(); return d ? (d.size ?? "") : 10000; });
  const [currency, setCurrency]             = useState(() => { const d = loadDesignFromStorage(); return d ? (d.currency || "£") : "£"; });

  const [locked, setLocked]                 = useState(() => { const d = loadDesignFromStorage(); return d ? (d.locked || false) : false; });
  const [pinnedOrder, setPinnedOrder]       = useState([]);

  const [csvImportPreview, setCsvImportPreview] = useState(null);
  const [csvImportError,   setCsvImportError]   = useState(null);

  const [rebalOuterR, setRebalOuterR]       = useState(() => Array(5).fill(REBAL_OUTER_BASE));
  const [rebalValues, setRebalValues]       = useState(() => Array(5).fill(""));
  const [rebalTrades, setRebalTrades]         = useState(null);
  const [hasEverRebalanced, setHasEverRebalanced] = useState(false);
  const [rebalError, setRebalError]           = useState(null);

  const [trackAccounts,  setTrackAccounts]  = useState([{ id: 1, name: "Example account" }]);
  const [trackTxs,       setTrackTxs]       = useState([]);
  const [trackSnaps,     setTrackSnaps]     = useState([]);
  const [activeTrackAcc, setActiveTrackAcc] = useState(1);
  const [txType,         setTxType]         = useState("deposit");
  const [txAmount,       setTxAmount]       = useState("");
  const [txDate,         setTxDate]         = useState(() => new Date().toISOString().slice(0, 10));
  const [txToAcc,        setTxToAcc]        = useState(null);
  const [txEditDate,     setTxEditDate]     = useState(false);
  const [snapAmount,     setSnapAmount]     = useState("");
  const [snapDate,       setSnapDate]       = useState(() => new Date().toISOString().slice(0, 10));
  const [snapEditDate,   setSnapEditDate]   = useState(false);
  const [editingAccName, setEditingAccName] = useState(null);
  const [editAccNameVal, setEditAccNameVal] = useState("");
  const [chartDims, setChartDims] = useState({ w: 560, h: 210 });
  const chartRoRef = useRef(null);
  const chartContainerRef = useCallback(node => {
    if (chartRoRef.current) { chartRoRef.current.disconnect(); chartRoRef.current = null; }
    if (!node) return;
    const ro = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      if (width > 0 && height > 0) setChartDims({ w: Math.round(width), h: Math.round(height) });
    });
    ro.observe(node);
    chartRoRef.current = ro;
  }, []);
  const [trackSnapCount, setTrackSnapCount] = useState(() => parseInt(localStorage.getItem("trackSnapCount") || "0", 10));
  const [trackTxCount,   setTrackTxCount]   = useState(() => parseInt(localStorage.getItem("trackTxCount")  || "0", 10));
  const [rebalCount,     setRebalCount]     = useState(() => parseInt(localStorage.getItem("rebalCount")    || "0", 10));
  const [ledgerSort,           setLedgerSort]           = useState({ col: "date", dir: "desc" });
  const [ledgerTypeFilter,     setLedgerTypeFilter]     = useState(null);
  const [ledgerTypeFilterOpen, setLedgerTypeFilterOpen] = useState(false);
  const [evMarkerHover,        setEvMarkerHover]        = useState(null);
  const [deleteAccConfirm,     setDeleteAccConfirm]     = useState(null);
  const [deleteEntryConfirm,   setDeleteEntryConfirm]   = useState(null);
  const [editingLedgerCell,    setEditingLedgerCell]    = useState(null);
  const [editingLedgerVal,     setEditingLedgerVal]     = useState("");

  const [insight, setInsight]               = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);
  const [insightError, setInsightError]     = useState(null);
  const [insightElapsed, setInsightElapsed] = useState(0);
  const [lastPortfolio, setLastPortfolio]   = useState(null);
  const [insightCount, setInsightCount]     = useState(() =>
    parseInt(localStorage.getItem("insightCount") || "0", 10)
  );
  const [insightDisclaimerOpen,    setInsightDisclaimerOpen]    = useState(false);
  const [insightDisclaimerChecked, setInsightDisclaimerChecked] = useState(false);
  const [hideInsightDisclaimer,    setHideInsightDisclaimer]    = useState(() =>
    localStorage.getItem("hideInsightDisclaimer") === "true"
  );

  const [session,       setSession]       = useState(() => {
    try { return JSON.parse(localStorage.getItem("session") || "null"); } catch { return null; }
  });
  const [subscribed,       setSubscribed]       = useState(false);
  const [checkoutSuccess,  setCheckoutSuccess]  = useState(false);
  const [cookieBanner,     setCookieBanner]     = useState(localStorage.getItem('meedo_cookie_consent') !== 'accepted' && !sessionStorage.getItem('meedo_cookie_dismissed'));
  const [authModalOpen,    setAuthModalOpen]    = useState(false);
  const [authEmail,        setAuthEmail]        = useState("");
  const [authSending,   setAuthSending]   = useState(false);
  const [authSent,      setAuthSent]      = useState(false);
  const [authError,     setAuthError]     = useState(null);
  const [authVerifying, setAuthVerifying] = useState(() => {
    return new URLSearchParams(window.location.search).has("token");
  });

  const [feedbackOpen,    setFeedbackOpen]    = useState(false);
  const [feedbackText,    setFeedbackText]    = useState("");
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [feedbackSent,    setFeedbackSent]    = useState(false);
  const [feedbackError,   setFeedbackError]   = useState(null);

  const [tooltipsEnabled, setTooltipsEnabled] = useState(() => {
    const stored = localStorage.getItem("tooltipsEnabled");
    return stored === null ? true : stored === "true";
  });

  const historyRef = useRef((() => { const d = loadDesignFromStorage(); return [{ boundaries: d ? d.boundaries : initBoundaries(3), segmentLabels: d ? d.labels : ["Enter investment name / ticker", "Enter investment name / ticker", "Enter investment name / ticker"], n: d ? d.n : 3 }]; })());
  const historyPos = useRef(0);

  const svgRef              = useRef(null);
  const insightPanelRef     = useRef(null);
  const inputRef            = useRef(null);
  const labelInputRef       = useRef(null);
  const trackIdRef          = useRef(1);
  const designSaveTimerRef  = useRef(null);
  const csvImportInputRef   = useRef(null);

  const segmentData = computeSegments(boundaries);
  const pcts        = sweepToPct(segmentData);

  const portfolioSizeRef = useRef(portfolioSize);
  portfolioSizeRef.current = portfolioSize;
  const pctsRef = useRef(pcts);
  pctsRef.current = pcts;

  const rebalPortfolioVal = parseFloat(portfolioSize) || 0;
  const rebalTotalCurrent = rebalValues.reduce((s, v, i) => {
    const val = v !== "" ? parseFloat(v) || 0 : rebalPortfolioVal * pcts[i] / 100;
    return s + val;
  }, 0);

  const handleGetInsight = async () => {
    if (!subscribed && insightCount >= INSIGHT_LIMIT) return;

    const portfolio = segmentLabels.map((label, i) => ({ label, percentage: pcts[i] }));

    // Check materiality — skip API call if nothing changed by ≥ 3%
    if (insight && lastPortfolio && lastPortfolio.length === portfolio.length) {
      const isMaterial = portfolio.some((seg, i) =>
        seg.label !== lastPortfolio[i].label ||
        Math.abs(seg.percentage - lastPortfolio[i].percentage) >= MATERIALITY_THRESHOLD
      );
      if (!isMaterial) return;
    }

    setInsightLoading(true);
    setInsightError(null);
    setInsight(null);
    if (isMobile && insightPanelRef.current) {
      insightPanelRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    try {
      const response = await fetch("/api/insight", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ portfolio, stream: true }),
      });

      if (!response.ok || !response.body) throw new Error("API error");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let full = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        full += decoder.decode(value, { stream: true });
        setInsight(full);
      }
      if (!full) throw new Error("Empty response");

      setLastPortfolio(portfolio);
      const newCount = insightCount + 1;
      setInsightCount(newCount);
      localStorage.setItem("insightCount", newCount.toString());
    } catch {
      setInsight(null);
      setInsightError("Something went wrong. Please try again.");
    } finally {
      setInsightLoading(false);
    }
  };

  const handleGetInsightClick = () => {
    if (!subscribed && insightCount >= INSIGHT_LIMIT) { openCheckout(); return; }
    if (hideInsightDisclaimer) { handleGetInsight(); return; }
    setInsightDisclaimerChecked(false);
    setInsightDisclaimerOpen(true);
  };

  const handleInsightDisclaimerUnderstand = () => {
    if (insightDisclaimerChecked) {
      localStorage.setItem("hideInsightDisclaimer", "true");
      setHideInsightDisclaimer(true);
    }
    setInsightDisclaimerOpen(false);
    handleGetInsight();
  };

  const handleInsightDisclaimerDecline = () => {
    setInsightDisclaimerOpen(false);
  };

  const handleImportCsvClick = () => {
    csvImportInputRef.current?.click();
  };

  const handleCsvFileSelected = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const { data, meta, errors } = Papa.parse(String(reader.result || ""), {
        header: true,
        skipEmptyLines: true,
      });
      if (errors?.length || !data?.length) {
        setCsvImportError("Couldn't read that file — please check it's a valid CSV.");
        return;
      }
      const holdings = csvRowsToHoldings(data, meta.fields || []);
      if (!holdings) {
        setCsvImportError("Couldn't find investment names and allocations or values in that file. Make sure it has columns like \"Name\"/\"Ticker\" and \"Allocation %\"/\"Value\".");
        return;
      }
      setCsvImportPreview(consolidateHoldings(holdings, MAX_IMPORT_SEGMENTS));
    };
    reader.onerror = () => setCsvImportError("Couldn't read that file — please try again.");
    reader.readAsText(file);
  };

  const handleCsvImportConfirm = () => {
    if (!csvImportPreview) return;
    const { boundaries: newBoundaries, labels: newLabels, n: newN } = holdingsToDesign(csvImportPreview);
    setBoundaries(newBoundaries);
    setSegmentLabels(newLabels);
    setN(newN);
    setPinnedOrder([]);
    pushHistory(newBoundaries, newLabels, newN);
    setCsvImportPreview(null);
  };

  const pushHistory = useCallback((newBoundaries, newLabels, newN) => {
    const snapshot   = { boundaries: newBoundaries, segmentLabels: newLabels, n: newN };
    const newHistory = historyRef.current.slice(0, historyPos.current + 1);
    newHistory.push(snapshot);
    if (newHistory.length > MAX_HISTORY) newHistory.shift();
    historyRef.current = newHistory;
    historyPos.current = newHistory.length - 1;
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token  = params.get("token");
    if (!token) return;
    window.history.replaceState({}, "", window.location.pathname);
    fetch(`/api/auth/verify?token=${token}`)
      .then(r => r.json())
      .then(data => {
        if (data.sessionToken) {
          const sess = { sessionToken: data.sessionToken, user: data.user };
          localStorage.setItem("session", JSON.stringify(sess));
          setSession(sess);
          window.gtag?.('event', 'login_complete');
        } else {
          setAuthError("Login link is invalid or has expired. Please request a new one.");
        }
      })
      .catch(() => setAuthError("Something went wrong. Please try again."))
      .finally(() => setAuthVerifying(false));
  }, []);

  // Handle ?checkout_session= redirect from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("checkout_session");
    if (!sessionId) return;
    localStorage.setItem("pendingCheckoutSession", sessionId);
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  useEffect(() => {
    if (!session) return;
    const headers = { "Content-Type": "application/json", "Authorization": `Bearer ${session.sessionToken}` };

    // Fetch subscription status
    fetch("/api/subscription", { headers })
      .then(r => r.json())
      .then(data => {
        const isSubscribed = !!data.subscribed;
        setSubscribed(isSubscribed);
        window.gtag?.('set', 'user_properties', { user_type: isSubscribed ? 'subscribed' : 'free' });
      })
      .catch(() => {});

    // Link pending Stripe checkout session if present
    const pendingSession = localStorage.getItem("pendingCheckoutSession");
    if (pendingSession) {
      fetch("/api/subscription/link", { method: "POST", headers, body: JSON.stringify({ session_id: pendingSession }) })
        .then(r => r.json())
        .then(data => {
          if (data.ok) {
            localStorage.removeItem("pendingCheckoutSession");
            setSubscribed(true);
            setCheckoutSuccess(true);
            window.gtag?.('event', 'subscription_complete', { origin: 'app' });
          }
        })
        .catch(() => {});
    }

    // Sync + load Track data
    fetch("/api/sync", {
      method: "POST", headers,
      body: JSON.stringify({ accounts: trackAccounts, snapshots: trackSnaps, transactions: trackTxs }),
    })
    .then(() => Promise.all([
      fetch("/api/accounts",     { headers }).then(r => r.json()),
      fetch("/api/snapshots",    { headers }).then(r => r.json()),
      fetch("/api/transactions", { headers }).then(r => r.json()),
    ]))
    .then(([accs, snaps, txs]) => {
      if (!accs || accs.length === 0) return;
      setTrackAccounts(accs.map(a => ({ id: a.id, name: a.name })));
      setTrackSnaps(snaps.map(s => ({ id: s.id, accountId: s.account_id, value: s.value, date: s.date })));
      setTrackTxs(txs.map(t => ({ id: t.id, accountId: t.account_id, type: t.type, amount: t.amount, date: t.date, ...(t.to_account_id ? { toAccountId: t.to_account_id } : {}) })));
      const maxId = Math.max(0, ...accs.map(a => a.id), ...snaps.map(s => s.id), ...txs.map(t => t.id));
      trackIdRef.current = maxId;
      setActiveTrackAcc(accs[0].id);
    })
    .catch(() => {});

    // Load or sync portfolio design
    fetch("/api/portfolio", { headers })
      .then(r => r.json())
      .then(design => {
        if (design && design.boundaries) {
          // D1 has data — use it
          setN(design.n);
          setBoundaries(design.boundaries);
          setSegmentLabels(design.labels);
          setPortfolioName(design.name || "");
          setPortfolioSize(design.size ?? "");
          setCurrency(design.currency || "£");
          setLocked(design.locked || false);
          historyRef.current = [{ boundaries: design.boundaries, segmentLabels: design.labels, n: design.n }];
        } else {
          // D1 empty — push current state up
          const current = { n, boundaries, labels: segmentLabels, name: portfolioName, size: portfolioSize || null, currency, locked };
          fetch("/api/portfolio", { method: "PUT", headers, body: JSON.stringify(current) }).catch(() => {});
        }
      })
      .catch(() => {});
  }, [session?.sessionToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendMagicLink = async () => {
    setAuthSending(true);
    setAuthError(null);
    try {
      const res  = await fetch("/api/auth/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: authEmail }),
      });
      const data = await res.json();
      if (data.ok) { setAuthSent(true); }
      else { setAuthError(data.error || "Something went wrong"); }
    } catch {
      setAuthError("Something went wrong. Please try again.");
    } finally {
      setAuthSending(false);
    }
  };

  const sendFeedback = async () => {
    if (!feedbackText.trim()) return;
    setFeedbackSending(true);
    setFeedbackError(null);
    try {
      const headers = { "Content-Type": "application/json" };
      if (session) headers["Authorization"] = `Bearer ${session.sessionToken}`;
      const res = await fetch("/api/feedback", { method: "POST", headers, body: JSON.stringify({ message: feedbackText.trim() }) });
      if (!res.ok) throw new Error();
      setFeedbackSent(true);
      setFeedbackText("");
    } catch {
      setFeedbackError("Something went wrong — please try again.");
    } finally {
      setFeedbackSending(false);
    }
  };

  const acceptCookies = () => {
    localStorage.setItem('meedo_cookie_consent', 'accepted');
    window.gtag?.('consent', 'update', { analytics_storage: 'granted' });
    setCookieBanner(false);
  };
  const rejectCookies = () => {
    sessionStorage.setItem('meedo_cookie_dismissed', 'true');
    window.gtag?.('consent', 'update', { analytics_storage: 'denied' });
    setCookieBanner(false);
  };

  const openCheckout = async () => {
    window.gtag?.('event', 'subscribe_click', { source: 'app', page: activePage });
    try {
      const res = await fetch("/api/stripe/create-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: session?.user?.email }),
      });
      const { url } = await res.json();
      if (url) window.location.href = url;
    } catch {}
  };

  const signOut = () => {
    localStorage.removeItem("session");
    setSession(null);
    setSubscribed(false);
    setAuthSent(false);
    setAuthEmail("");
    setAuthError(null);
    setTrackAccounts([{ id: 1, name: "Example account" }]);
    setTrackSnaps([]);
    setTrackTxs([]);
    setActiveTrackAcc(1);
    trackIdRef.current = 1;
  };

  useEffect(() => {
    const handleKey = (e) => {
      const isMac = navigator.platform.toUpperCase().includes("MAC");
      const mod   = isMac ? e.metaKey : e.ctrlKey;
      if (!mod) return;
      if (e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        if (historyPos.current <= 0) return;
        historyPos.current -= 1;
        const snap = historyRef.current[historyPos.current];
        setBoundaries(snap.boundaries);
        setSegmentLabels(snap.segmentLabels);
        setN(snap.n);
      }
      if (e.key === "z" && e.shiftKey) {
        e.preventDefault();
        if (historyPos.current >= historyRef.current.length - 1) return;
        historyPos.current += 1;
        const snap = historyRef.current[historyPos.current];
        setBoundaries(snap.boundaries);
        setSegmentLabels(snap.segmentLabels);
        setN(snap.n);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  useEffect(() => {
    if (editingIndex !== null && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingIndex]);

  useEffect(() => {
    if (editingLabelIndex !== null && labelInputRef.current) {
      labelInputRef.current.focus();
      labelInputRef.current.select();
    }
  }, [editingLabelIndex]);

  useEffect(() => {
    if (!insightLoading) return;
    setInsightElapsed(0);
    const id = setInterval(() => setInsightElapsed((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [insightLoading]);

  useEffect(() => {
    setRebalOuterR(Array(n).fill(REBAL_OUTER_BASE));
    setRebalValues(Array(n).fill(""));
  }, [n]);

  useEffect(() => {
    setRebalTrades(prev => prev !== null ? null : prev);
  }, [boundaries]);

  const handleAutoRebalance = () => {
    if (!portfolioSize) { setRebalError("Please enter a total portfolio investment amount to calculate rebalance values."); return; }
    setRebalError(null);
    if (rebalTotalCurrent === 0) return;
    const trades = pcts.map((pct, i) => {
      const effectiveVal = rebalValues[i] !== "" ? parseFloat(rebalValues[i]) || 0 : rebalPortfolioVal * pct / 100;
      const tradeTarget  = Math.round(rebalTotalCurrent * pct / 100);
      return { label: segmentLabels[i], trade: Math.round(tradeTarget - effectiveVal) };
    });
    setRebalTrades(trades);
    setHasEverRebalanced(true);
    setPortfolioSize(String(Math.round(rebalTotalCurrent)));
    setRebalOuterR(Array(n).fill(REBAL_OUTER_BASE));
    setRebalValues(Array(n).fill(""));
    setRebalCount(prev => { const next = prev + 1; localStorage.setItem("rebalCount", next); return next; });
  };

  const handleRebalValue = (i, val) => {
    setRebalValues(prev => prev.map((v, idx) => idx === i ? val : v));
    const portfolioVal = parseFloat(portfolioSize) || 0;
    if (portfolioVal === 0 || val === "") {
      setRebalOuterR(prev => prev.map((r, idx) => idx === i ? REBAL_OUTER_BASE : r));
      return;
    }
    const targetVal    = portfolioVal * pcts[i] / 100;
    const currentVal   = parseFloat(val) || 0;
    const ratio        = currentVal / targetVal;
    const BASE_THICKNESS = REBAL_OUTER_BASE - REBAL_INNER;
    const newR = Math.max(REBAL_MIN_OUTER, Math.min(REBAL_MAX_OUTER, REBAL_INNER + BASE_THICKNESS * ratio));
    setRebalOuterR(prev => prev.map((r, idx) => idx === i ? newR : r));
  };


  const commitLabelEdit = (index) => {
    if (editLabelValue.trim() !== "") {
      const newLabels = segmentLabels.map((l, i) => i === index ? editLabelValue.trim() : l);
      setSegmentLabels(newLabels);
      pushHistory(boundaries, newLabels, n);
    }
    setEditingLabelIndex(null);
    setEditLabelValue("");
  };

  const commitEdit = (index) => {
    const parsed = parseFloat(editValue);
    if (!isNaN(parsed) && parsed > 0 && parsed < 100) {
      // Build pin queue: remove existing entry for this index, push to end
      let queue = pinnedOrder.filter(p => p.index !== index);
      queue = [...queue, { index, pct: parsed }];

      // Evict oldest pins while their sum exceeds 100 (always preserve current edit)
      while (queue.length > 1 && queue.reduce((s, p) => s + p.pct, 0) > 100) {
        queue = queue.slice(1);
      }

      // Identify unpinned segments
      const pinnedIndices = new Set(queue.map(p => p.index));
      const pinnedSum     = queue.reduce((s, p) => s + p.pct, 0);
      const remainingPct  = 100 - pinnedSum;
      const unpinnedIs    = Array.from({ length: n }, (_, i) => i).filter(i => !pinnedIndices.has(i));

      // Compute desired pcts for all segments
      let desiredPcts;
      if (unpinnedIs.length === 0) {
        // All pinned — FIFO evict oldest, give it the remainder
        queue = queue.slice(1);
        const newPinnedIs  = new Set(queue.map(p => p.index));
        const newPinnedSum = queue.reduce((s, p) => s + p.pct, 0);
        desiredPcts = Array.from({ length: n }, (_, i) =>
          newPinnedIs.has(i) ? queue.find(p => p.index === i).pct : 100 - newPinnedSum
        );
      } else {
        // Distribute remainder proportionally among unpinned segments
        const unpinnedSum = unpinnedIs.reduce((s, i) => s + pcts[i], 0);
        desiredPcts = Array.from({ length: n }, (_, i) => {
          if (pinnedIndices.has(i)) return queue.find(p => p.index === i).pct;
          if (unpinnedSum === 0) return remainingPct / unpinnedIs.length;
          return (pcts[i] / unpinnedSum) * remainingPct;
        });
      }

      // Reject if any segment would fall below minimum sweep
      const minPct = MIN_PCT;
      if (desiredPcts.some(p => p < minPct)) {
        setEditingIndex(null);
        setEditValue("");
        return;
      }

      // Normalise (eliminates floating-point drift) and rebuild boundaries
      const pctTotal       = desiredPcts.reduce((s, p) => s + p, 0);
      const normalizedPcts = desiredPcts.map(p => p * 100 / pctTotal);
      const newBoundaries  = recalcBoundaries(boundaries, normalizedPcts, n);
      setBoundaries(newBoundaries);
      setPinnedOrder(queue);
      pushHistory(newBoundaries, segmentLabels, n);
    }
    setEditingIndex(null);
    setEditValue("");
  };

  const onTouchMove = useCallback((e) => {
    if (draggingHandle === null) return;
    if (e.touches.length !== 1) return;
    e.preventDefault();
    const angle = touchToAngle(e, svgRef.current);
    setBoundaries((prev) => {
      const n     = prev.length;
      const i     = draggingHandle;
      const prevB = prev[(i - 1 + n) % n];
      const nextB = prev[(i + 1) % n];
      const minSweepDeg = (MIN_PCT / 100) * (360 - n * GAP_DEGREES);
      let minAngle = (prevB + GAP_DEGREES + minSweepDeg) % 360;
      let maxAngle = (nextB - GAP_DEGREES - minSweepDeg + 360) % 360;
      let shifted  = (angle - minAngle + 360) % 360;
      let window   = (maxAngle - minAngle + 360) % 360;
      if (shifted > window) shifted = shifted > window / 2 + 180 ? 0 : window;
      const clamped = (minAngle + shifted) % 360;
      return prev.map((b, idx) => idx === i ? clamped : b);
    });
  }, [draggingHandle]);

  const onTouchEnd = useCallback(() => {
    if (draggingHandle !== null) {
      const prevSegI  = (draggingHandle - 1 + n) % n;
      const affectedI = draggingHandle % n;
      setBoundaries((prev) => {
        const currentPcts = sweepToPct(computeSegments(prev));
        setPinnedOrder(prevQueue => {
          let queue = prevQueue.filter(p => p.index !== prevSegI && p.index !== affectedI);
          queue = [...queue,
            { index: prevSegI,  pct: currentPcts[prevSegI]  },
            { index: affectedI, pct: currentPcts[affectedI] },
          ];
          return queue;
        });
        pushHistory(prev, segmentLabels, n);
        return prev;
      });
    }
    setDraggingHandle(null);
  }, [draggingHandle, segmentLabels, n, pushHistory]);

  const onMouseMove = useCallback((e) => {
    if (draggingHandle === null) return;
    const angle = mouseToAngle(e, svgRef.current);
    setBoundaries((prev) => {
      const n     = prev.length;
      const i     = draggingHandle;
      const prevB = prev[(i - 1 + n) % n];
      const nextB = prev[(i + 1) % n];
      const minSweepDeg = (MIN_PCT / 100) * (360 - n * GAP_DEGREES);
      let minAngle = (prevB + GAP_DEGREES + minSweepDeg) % 360;
      let maxAngle = (nextB - GAP_DEGREES - minSweepDeg + 360) % 360;
      let shifted  = (angle - minAngle + 360) % 360;
      let window   = (maxAngle - minAngle + 360) % 360;
      if (shifted > window) shifted = shifted > window / 2 + 180 ? 0 : window;
      const clamped = (minAngle + shifted) % 360;
      return prev.map((b, idx) => idx === i ? clamped : b);
    });
  }, [draggingHandle]);

  const onMouseUp = useCallback(() => {
    if (draggingHandle !== null) {
      const prevSegI  = (draggingHandle - 1 + n) % n;
      const affectedI = draggingHandle % n;
      setBoundaries((prev) => {
        const currentPcts = sweepToPct(computeSegments(prev));
        setPinnedOrder(prevQueue => {
          let queue = prevQueue.filter(p => p.index !== prevSegI && p.index !== affectedI);
          queue = [...queue,
            { index: prevSegI,  pct: currentPcts[prevSegI]  },
            { index: affectedI, pct: currentPcts[affectedI] },
          ];
          return queue;
        });
        pushHistory(prev, segmentLabels, n);
        return prev;
      });
    }
    setDraggingHandle(null);
    document.body.style.cursor = "";
  }, [draggingHandle, segmentLabels, n, pushHistory]);

  useEffect(() => {
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onTouchEnd);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [onMouseMove, onMouseUp, onTouchMove, onTouchEnd]);


  const addSegment = () => {
    const newN       = n + 1;
    const newLabels  = [...segmentLabels, "Label"];
    const currentPcts = sweepToPct(segmentData);
    const newSegPct  = 100 / newN;

    let queue       = [...pinnedOrder];
    const pinnedSet  = new Set(queue.map(p => p.index));
    const pinnedSum  = queue.reduce((s, p) => s + p.pct, 0);
    const unpinnedIs = Array.from({ length: n }, (_, i) => i).filter(i => !pinnedSet.has(i));

    let newPcts;
    if (unpinnedIs.length > 0) {
      const unpinnedSum          = unpinnedIs.reduce((s, i) => s + currentPcts[i], 0);
      const remainingForUnpinned = 100 - newSegPct - pinnedSum;
      newPcts = Array.from({ length: newN }, (_, i) => {
        if (i === n)              return newSegPct;
        if (pinnedSet.has(i))     return queue.find(p => p.index === i).pct;
        if (unpinnedSum === 0)    return remainingForUnpinned / unpinnedIs.length;
        return (currentPcts[i] / unpinnedSum) * remainingForUnpinned;
      });
    } else {
      // All existing segments pinned — FIFO evict oldest to absorb
      queue        = queue.slice(1);
      const newPinnedSet = new Set(queue.map(p => p.index));
      const newPinnedSum = queue.reduce((s, p) => s + p.pct, 0);
      newPcts = Array.from({ length: newN }, (_, i) => {
        if (i === n)             return newSegPct;
        if (newPinnedSet.has(i)) return queue.find(p => p.index === i).pct;
        return 100 - newPinnedSum - newSegPct;
      });
    }

    const clamped    = newPcts.map(p => Math.max(MIN_PCT, p));
    const total      = clamped.reduce((s, p) => s + p, 0);
    const normalized = clamped.map(p => p * 100 / total);
    const newBoundaries = recalcBoundaries(initBoundaries(newN), normalized, newN);

    setBoundaries(newBoundaries);
    setSegmentLabels(newLabels);
    setN(newN);
    setPinnedOrder(queue);
    pushHistory(newBoundaries, newLabels, newN);
  };

  const removeSegment = () => {
    if (n <= 2) return;
    const newN        = n - 1;
    const newLabels   = segmentLabels.slice(0, -1);
    const removedIdx  = n - 1;
    const currentPcts = sweepToPct(segmentData);
    const freedPct    = currentPcts[removedIdx];

    let queue        = pinnedOrder.filter(p => p.index !== removedIdx);
    const pinnedSet   = new Set(queue.map(p => p.index));
    const unpinnedIs  = Array.from({ length: newN }, (_, i) => i).filter(i => !pinnedSet.has(i));

    let newPcts;
    if (unpinnedIs.length > 0) {
      const unpinnedSum      = unpinnedIs.reduce((s, i) => s + currentPcts[i], 0);
      const totalForUnpinned = unpinnedSum + freedPct;
      newPcts = Array.from({ length: newN }, (_, i) => {
        if (pinnedSet.has(i))  return queue.find(p => p.index === i).pct;
        if (unpinnedSum === 0) return totalForUnpinned / unpinnedIs.length;
        return (currentPcts[i] / unpinnedSum) * totalForUnpinned;
      });
    } else if (queue.length > 0) {
      // All remaining pinned — give freed space to oldest
      const oldestIdx = queue[0].index;
      newPcts = Array.from({ length: newN }, (_, i) => {
        const pin = queue.find(p => p.index === i);
        return i === oldestIdx ? pin.pct + freedPct : pin?.pct ?? 100 / newN;
      });
    } else {
      newPcts = Array.from({ length: newN }, () => 100 / newN);
    }

    const clamped    = newPcts.map(p => Math.max(MIN_PCT, p));
    const total      = clamped.reduce((s, p) => s + p, 0);
    const normalized = clamped.map(p => p * 100 / total);
    const newBoundaries = recalcBoundaries(initBoundaries(newN), normalized, newN);

    setBoundaries(newBoundaries);
    setSegmentLabels(newLabels);
    setN(newN);
    setPinnedOrder(queue);
    pushHistory(newBoundaries, newLabels, newN);
  };

  // ─── Track helpers ───────────────────────────────────────────────────────────
  const fmtDate = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return `${parseInt(d)} ${months[parseInt(m) - 1]} ${y}`;
  };

  const dbFetch = (url, options = {}) => fetch(url, {
    ...options,
    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${session?.sessionToken}`, ...(options.headers || {}) },
  });

  useEffect(() => {
    const data = { n, boundaries, labels: segmentLabels, name: portfolioName, size: portfolioSize || null, currency, locked };
    clearTimeout(designSaveTimerRef.current);
    designSaveTimerRef.current = setTimeout(() => {
      localStorage.setItem("portfolioDesign", JSON.stringify(data));
      if (session) {
        dbFetch("/api/portfolio", { method: "PUT", body: JSON.stringify(data) }).catch(() => {});
      }
    }, 800);
  }, [n, boundaries, segmentLabels, portfolioName, portfolioSize, currency, locked]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (portfolioSize) setRebalError(null); }, [portfolioSize]);

  const addTrackTx = () => {
    const amt = parseFloat(txAmount);
    if (!amt || amt <= 0) return;
    if (txType === "transfer" && !txToAcc) return;
    const fromAccId = activeTrackAcc ?? trackAccounts[0]?.id;
    if (!fromAccId) return;
    const newTx = { accountId: fromAccId, type: txType, amount: amt, date: txDate, ...(txType === "transfer" ? { toAccountId: txToAcc } : {}) };
    if (session) {
      dbFetch("/api/transactions", { method: "POST", body: JSON.stringify({ accountId: fromAccId, type: txType, amount: amt, date: txDate, toAccountId: txType === "transfer" ? txToAcc : null }) })
        .then(r => r.json())
        .then(data => { if (data.id) setTrackTxs(prev => [...prev, { ...newTx, id: data.id }]); })
        .catch(() => setTrackTxs(prev => [...prev, { ...newTx, id: ++trackIdRef.current }]));
    } else {
      setTrackTxs(prev => [...prev, { ...newTx, id: ++trackIdRef.current }]);
    }
    setTxAmount("");
    setTrackTxCount(prev => { const next = prev + 1; localStorage.setItem("trackTxCount", next); return next; });
  };

  const addTrackSnap = () => {
    const val = parseFloat(snapAmount);
    if (isNaN(val) || val < 0) return;
    const accId = activeTrackAcc ?? trackAccounts[0]?.id;
    if (!accId) return;
    const newSnap = { accountId: accId, value: val, date: snapDate };
    if (session) {
      dbFetch("/api/snapshots", { method: "POST", body: JSON.stringify(newSnap) })
        .then(r => r.json())
        .then(data => { if (data.id) setTrackSnaps(prev => [...prev, { ...newSnap, id: data.id }]); })
        .catch(() => setTrackSnaps(prev => [...prev, { ...newSnap, id: ++trackIdRef.current }]));
    } else {
      setTrackSnaps(prev => [...prev, { ...newSnap, id: ++trackIdRef.current }]);
    }
    setSnapAmount("");
    setTrackSnapCount(prev => { const next = prev + 1; localStorage.setItem("trackSnapCount", next); return next; });
  };

  const addTrackAccount = () => {
    if (trackAccounts.length >= 20) return;
    const name = `Account ${trackAccounts.length + 1}`;
    if (session) {
      dbFetch("/api/accounts", { method: "POST", body: JSON.stringify({ name }) })
        .then(r => r.json())
        .then(data => {
          if (data.id) {
            setTrackAccounts(prev => [...prev, { id: data.id, name: data.name }]);
            setActiveTrackAcc(data.id);
          }
        })
        .catch(() => {
          const newId = ++trackIdRef.current;
          setTrackAccounts(prev => [...prev, { id: newId, name }]);
          setActiveTrackAcc(newId);
        });
    } else {
      const newId = ++trackIdRef.current;
      setTrackAccounts(prev => [...prev, { id: newId, name }]);
      setActiveTrackAcc(newId);
    }
  };

  const deleteTrackAccount = (accId) => {
    const remaining = trackAccounts.filter(a => a.id !== accId);
    setTrackAccounts(remaining);
    setTrackTxs(prev => prev.filter(t => t.accountId !== accId && t.toAccountId !== accId));
    setTrackSnaps(prev => prev.filter(s => s.accountId !== accId));
    if (activeTrackAcc === accId) setActiveTrackAcc(remaining[0]?.id ?? null);
    if (session) dbFetch(`/api/accounts/${accId}`, { method: "DELETE" }).catch(() => {});
  };

  const deleteTrackEntry = (row) => {
    if (row.snapId !== null) {
      setTrackSnaps(prev => prev.filter(s => s.id !== row.snapId));
      if (session) dbFetch(`/api/snapshots/${row.snapId}`, { method: "DELETE" }).catch(() => {});
    } else {
      setTrackTxs(prev => prev.filter(t => t.id !== row.txId));
      if (session) dbFetch(`/api/transactions/${row.txId}`, { method: "DELETE" }).catch(() => {});
    }
  };

  const commitLedgerEdit = (row, field, val) => {
    if (field === "date") {
      if (!val) { setEditingLedgerCell(null); setEditingLedgerVal(""); return; }
      if (row.snapId !== null) {
        setTrackSnaps(prev => prev.map(s => s.id === row.snapId ? { ...s, date: val } : s));
        if (session) dbFetch(`/api/snapshots/${row.snapId}`, { method: "PUT", body: JSON.stringify({ date: val }) }).catch(() => {});
      } else {
        setTrackTxs(prev => prev.map(t => t.id === row.txId ? { ...t, date: val } : t));
        if (session) dbFetch(`/api/transactions/${row.txId}`, { method: "PUT", body: JSON.stringify({ date: val }) }).catch(() => {});
      }
    } else if (field === "type") {
      if (row.txId === null) { setEditingLedgerCell(null); setEditingLedgerVal(""); return; }
      setTrackTxs(prev => prev.map(t => t.id === row.txId ? { ...t, type: val } : t));
      if (session) dbFetch(`/api/transactions/${row.txId}`, { method: "PUT", body: JSON.stringify({ type: val }) }).catch(() => {});
    } else if (field === "amount") {
      const num = parseFloat(val);
      if (isNaN(num) || num <= 0) { setEditingLedgerCell(null); setEditingLedgerVal(""); return; }
      if (row.snapId !== null) {
        setTrackSnaps(prev => prev.map(s => s.id === row.snapId ? { ...s, value: num } : s));
        if (session) dbFetch(`/api/snapshots/${row.snapId}`, { method: "PUT", body: JSON.stringify({ value: num }) }).catch(() => {});
      } else {
        setTrackTxs(prev => prev.map(t => t.id === row.txId ? { ...t, amount: num } : t));
        if (session) dbFetch(`/api/transactions/${row.txId}`, { method: "PUT", body: JSON.stringify({ amount: num }) }).catch(() => {});
      }
    } else if (field === "account") {
      const num = parseInt(val, 10);
      if (isNaN(num)) { setEditingLedgerCell(null); setEditingLedgerVal(""); return; }
      if (row.kind === "transfer-in") {
        setTrackTxs(prev => prev.map(t => t.id === row.txId ? { ...t, toAccountId: num } : t));
        if (session) dbFetch(`/api/transactions/${row.txId}`, { method: "PUT", body: JSON.stringify({ toAccountId: num }) }).catch(() => {});
      } else if (row.snapId !== null) {
        setTrackSnaps(prev => prev.map(s => s.id === row.snapId ? { ...s, accountId: num } : s));
        if (session) dbFetch(`/api/snapshots/${row.snapId}`, { method: "PUT", body: JSON.stringify({ accountId: num }) }).catch(() => {});
      } else {
        setTrackTxs(prev => prev.map(t => t.id === row.txId ? { ...t, accountId: num } : t));
        if (session) dbFetch(`/api/transactions/${row.txId}`, { method: "PUT", body: JSON.stringify({ accountId: num }) }).catch(() => {});
      }
    }
    setEditingLedgerCell(null);
    setEditingLedgerVal("");
  };

  const commitAccName = (accId) => {
    const trimmed = editAccNameVal.trim();
    if (trimmed) {
      setTrackAccounts(prev => prev.map(a => a.id === accId ? { ...a, name: trimmed } : a));
      if (session) dbFetch(`/api/accounts/${accId}`, { method: "PUT", body: JSON.stringify({ name: trimmed }) }).catch(() => {});
    }
    setEditingAccName(null);
    setEditAccNameVal("");
  };

  if (authVerifying) {
    return (
      <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Inter, system-ui, sans-serif" }}>
        <p style={{ fontSize: 14, color: "#999", margin: 0 }}>Signing you in…</p>
      </div>
    );
  }

  return (
    <MantineProvider theme={createTheme({
      colors: {
        brand: ["#f0fffd","#d0fff9","#a8fff4","#7fffee","#71FFED","#5ef5e3","#71FFED","#4dd9c4","#32b39e","#1a8c79"],
      },
      primaryColor: "brand",
      primaryShade: 6,
      components: {
        Button: { styles: { label: { color: "#003129" } } },
        Switch: { defaultProps: { color: "#00DAC8" }, styles: { label: { color: "#003129" } } },
        Tooltip: { styles: { tooltip: { background: "#CAFFFB", color: "#003129" }, arrow: { background: "#CAFFFB" } } },
      },
    })}>
    <div style={{
      display: "flex",
      flexDirection: "column",
      height: "100vh",
      background: "white",
      fontFamily: "Inter, system-ui, sans-serif",
      fontSize: 14,
    }}>

      {/* Header */}
      <header style={{
        height: 52,
        flexShrink: 0,
        background: "white",
        borderBottom: "0.5px solid rgba(0,0,0,0.1)",
        overflowX: isMobile ? "auto" : "visible",
      }}>
        <div style={{ display: "flex", alignItems: "center", height: "100%", padding: isMobile ? "0 12px" : "0 24px", minWidth: isMobile ? "max-content" : undefined }}>
        <span style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: "#1a1a1a", letterSpacing: "0.01em" }}>
          <a href="https://meedo.uk" target="_blank" rel="noopener noreferrer" style={{ display: "flex", lineHeight: 0 }}>
            <svg width={isMobile ? 100 : 144} height={isMobile ? 19 : 28} viewBox="0 0 208 44" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd" d="M107.312 13.582C110.492 13.582 113.097 14.0991 115.126 15.1328C117.155 16.1665 118.651 17.8511 119.613 20.1865C120.576 22.4836 121.036 25.6037 120.994 29.5469H99.6562C99.7608 30.5423 100.054 31.4614 100.535 32.3037C101.037 33.146 101.801 33.8351 102.826 34.3711C103.872 34.8687 105.263 35.1182 106.999 35.1182C108.798 35.1182 110.273 34.8881 111.424 34.4287C112.595 33.9694 113.295 33.3185 113.525 32.4766H120.932C120.743 34.6969 120.136 36.6302 119.111 38.2764C118.107 39.9227 116.632 41.2052 114.687 42.124C112.762 43.0045 110.304 43.4453 107.312 43.4453C104.154 43.4453 101.424 42.9852 99.123 42.0664C96.8429 41.1476 95.0858 39.5972 93.8516 37.415C92.6174 35.2328 92 32.2848 92 28.5713C92 24.8959 92.5967 21.9665 93.7891 19.7842C95.0024 17.5638 96.7492 15.9747 99.0293 15.0176C101.309 14.0606 104.07 13.5821 107.312 13.582ZM106.999 21.9092C104.782 21.9092 103.087 22.3499 101.916 23.2305C100.765 24.111 100.085 24.9915 99.876 25.8721H113.683C113.536 24.8767 112.951 23.9768 111.926 23.1729C110.901 22.3306 109.258 21.9092 106.999 21.9092Z" fill="#003129"/>
            <path fillRule="evenodd" clipRule="evenodd" d="M136.312 13.582C139.492 13.582 142.097 14.0991 144.126 15.1328C146.155 16.1665 147.651 17.8511 148.613 20.1865C149.576 22.4836 150.036 25.6037 149.994 29.5469H128.656C128.761 30.5423 129.054 31.4614 129.535 32.3037C130.037 33.146 130.801 33.8351 131.826 34.3711C132.872 34.8687 134.263 35.1182 135.999 35.1182C137.798 35.1182 139.273 34.8881 140.424 34.4287C141.595 33.9694 142.295 33.3185 142.525 32.4766H149.932C149.743 34.6969 149.136 36.6302 148.111 38.2764C147.107 39.9227 145.632 41.2052 143.687 42.124C141.762 43.0045 139.304 43.4453 136.312 43.4453C133.154 43.4453 130.424 42.9852 128.123 42.0664C125.843 41.1476 124.086 39.5972 122.852 37.415C121.617 35.2328 121 32.2848 121 28.5713C121 24.8959 121.597 21.9665 122.789 19.7842C124.002 17.5638 125.749 15.9747 128.029 15.0176C130.309 14.0606 133.07 13.5821 136.312 13.582ZM135.999 21.9092C133.782 21.9092 132.087 22.3499 130.916 23.2305C129.765 24.111 129.085 24.9915 128.876 25.8721H142.683C142.536 24.8767 141.951 23.9768 140.926 23.1729C139.901 22.3306 138.258 21.9092 135.999 21.9092Z" fill="#003129"/>
            <path fillRule="evenodd" clipRule="evenodd" d="M194.697 0.0283203L194.699 2.24512C195.488 2.19463 196.18 2.23043 196.771 2.35547C197.64 2.52767 198.309 2.84006 198.777 3.29102C199.246 3.74205 199.523 4.28283 199.607 4.91309L196.284 5.41504C196.136 5.13418 195.894 4.95811 195.557 4.8877C195.219 4.81742 194.669 4.84001 193.907 4.95508C193.197 5.06243 192.686 5.20645 192.376 5.38672C192.065 5.55514 191.93 5.77605 191.971 6.04883C191.998 6.2265 192.096 6.35129 192.265 6.42285C192.442 6.4808 192.756 6.49413 193.203 6.46289C193.66 6.41814 194.322 6.33082 195.187 6.2002C195.784 6.11 196.37 6.04592 196.943 6.00781C197.527 5.96819 198.058 5.99049 198.534 6.07617C199.021 6.16041 199.423 6.34913 199.741 6.64062C200.057 6.92057 200.259 7.33976 200.343 7.89746C200.445 8.57372 200.285 9.15612 199.863 9.64453C199.452 10.1312 198.824 10.5421 197.979 10.876C197.133 11.198 196.111 11.4494 194.916 11.6299C194.831 11.6428 194.745 11.6535 194.661 11.665L194.655 13.4512C197.306 13.5132 199.524 14.0308 201.308 15.0029C203.232 16.0029 204.67 17.6188 205.623 19.8496C206.576 22.0418 207.052 24.9263 207.052 28.5029C207.052 32.0799 206.576 34.9649 205.623 37.1572C204.67 39.3494 203.231 40.9453 201.308 41.9453C199.402 42.9453 197.001 43.4453 194.106 43.4453C191.193 43.4453 188.765 42.9453 186.823 41.9453C184.9 40.9454 183.453 39.3492 182.481 37.1572C181.529 34.9649 181.052 32.0799 181.052 28.5029C181.052 24.9262 181.529 22.0419 182.481 19.8496C183.453 17.6189 184.899 16.0029 186.823 15.0029C188.66 14.021 190.931 13.5036 193.636 13.4502L193.642 11.7764C192.9 11.8332 192.256 11.8265 191.709 11.751C190.87 11.635 190.212 11.4298 189.736 11.1377C189.271 10.844 188.936 10.5123 188.732 10.1426C188.539 9.77125 188.435 9.43412 188.421 9.13281L191.837 8.61719C192.026 8.89199 192.332 9.06488 192.753 9.13477C193.183 9.1911 193.774 9.16326 194.525 9.0498C195.112 8.96116 195.54 8.85923 195.808 8.74609C196.085 8.63161 196.255 8.50868 196.32 8.37793C196.395 8.24537 196.424 8.11285 196.404 7.98242C196.374 7.78102 196.254 7.65317 196.046 7.59961C195.848 7.5447 195.521 7.54508 195.066 7.60156C194.61 7.6463 193.983 7.72885 193.19 7.84863C192.449 7.96062 191.773 8.02587 191.165 8.04492C190.568 8.06235 190.044 8.01448 189.595 7.90039C189.156 7.78464 188.805 7.59491 188.544 7.33105C188.281 7.05527 188.115 6.68521 188.045 6.22266C187.959 5.65327 188.048 5.15442 188.31 4.72656C188.57 4.28685 188.956 3.9181 189.47 3.62207C189.981 3.31428 190.586 3.05968 191.282 2.85742C191.989 2.6536 192.735 2.49228 193.518 2.37402C193.573 2.36569 193.628 2.35823 193.683 2.35059L193.681 0L194.697 0.0283203ZM194.106 21.8105C192.751 21.8106 191.596 22.0606 190.644 22.5605C189.709 23.0605 188.995 23.8106 188.5 24.8105C188.024 25.7721 187.785 27.003 187.785 28.5029C187.785 29.9645 188.024 31.1953 188.5 32.1953C188.976 33.1568 189.682 33.8877 190.616 34.3877C191.569 34.8492 192.732 35.08 194.106 35.0801C195.462 35.0801 196.599 34.8492 197.515 34.3877C198.431 33.8877 199.127 33.1568 199.604 32.1953C200.08 31.1953 200.318 29.9645 200.318 28.5029C200.318 27.0414 200.08 25.8105 199.604 24.8105C199.145 23.8106 198.458 23.0605 197.542 22.5605C196.626 22.0607 195.48 21.8105 194.106 21.8105Z" fill="#003129"/>
            <path fillRule="evenodd" clipRule="evenodd" d="M180.846 42.584H173.754C173.67 41.7034 173.576 40.2864 173.472 38.334C173.398 36.9222 173.351 35.3947 173.329 33.752C173.257 34.039 173.18 34.3223 173.095 34.6006C172.53 36.4 171.714 37.9705 170.647 39.3105C169.581 40.612 168.294 41.6261 166.788 42.3535C165.282 43.0427 163.587 43.3877 161.704 43.3877C159.445 43.3877 157.437 42.8135 155.68 41.665C153.922 40.4782 152.531 38.7743 151.506 36.5537C150.502 34.3332 150 31.653 150 28.5137C150 25.2213 150.502 22.4646 151.506 20.2441C152.531 18.0236 153.922 16.3574 155.68 15.2471C157.437 14.1369 159.445 13.5821 161.704 13.582C163.587 13.582 165.282 13.9454 166.788 14.6729C168.294 15.362 169.581 16.3769 170.647 17.7168C171.714 19.0184 172.53 20.5882 173.095 22.4258C173.174 22.6841 173.246 22.947 173.314 23.2129V2.95801H180.846V42.584ZM164.528 22.3682C162.395 22.3682 160.742 22.8852 159.57 23.9189C158.399 24.9144 157.813 26.4462 157.813 28.5137C157.813 29.8534 158.075 30.9826 158.598 31.9014C159.121 32.8202 159.885 33.5093 160.889 33.9688C161.893 34.4282 163.106 34.6582 164.528 34.6582C166.118 34.6582 167.572 34.4861 168.89 34.1416C170.228 33.7587 171.296 33.1267 172.091 32.2461C172.907 31.3656 173.314 30.1215 173.314 28.5137C173.314 26.8676 172.906 25.6042 172.091 24.7236C171.296 23.8431 170.228 23.2303 168.89 22.8857C167.572 22.5412 166.118 22.3682 164.528 22.3682Z" fill="#003129"/>
            <path d="M6 2.95801C6.82843 2.95801 7.5 3.62958 7.5 4.45801V6.35547H8.7998C9.91991 6.35547 10.4804 6.35526 10.9082 6.57324C11.2844 6.76498 11.5905 7.07104 11.7822 7.44727C12.0001 7.87507 12 8.43572 12 9.55566V27.8223C12 28.9418 12 29.502 11.7822 29.9297C11.5905 30.3058 11.2843 30.612 10.9082 30.8037C10.4804 31.0217 9.91983 31.0225 8.7998 31.0225H7.5V41.458C7.49988 42.2863 6.82835 42.958 6 42.958C5.17165 42.958 4.50012 42.2863 4.5 41.458V31.0225H3.2002C2.08017 31.0225 1.51961 31.0217 1.0918 30.8037C0.715689 30.612 0.409468 30.3058 0.217773 29.9297C1.66297e-05 29.502 1.90553e-09 28.9418 1.48518e-10 27.8223V9.55566C1.48518e-10 8.43572 -0.000120893 7.87507 0.217773 7.44727C0.409489 7.07104 0.715584 6.76498 1.0918 6.57324C1.51962 6.35526 2.08009 6.35547 3.2002 6.35547H4.5V4.45801C4.5 3.62958 5.17157 2.95801 6 2.95801Z" fill="#003129"/>
            <path d="M20 14.3428C20.8284 14.3428 21.5 15.0143 21.5 15.8428V24.3809H22.7998C23.9199 24.3809 24.4804 24.3806 24.9082 24.5986C25.2844 24.7903 25.5905 25.0965 25.7822 25.4727C26.0001 25.9005 26 26.4611 26 27.5811V38.2578C26 39.3778 26.0002 39.9384 25.7822 40.3662C25.5905 40.7424 25.2844 41.0485 24.9082 41.2402C24.4804 41.4582 23.9198 41.458 22.7998 41.458H21.5C21.4999 42.2863 20.8284 42.958 20 42.958C19.1716 42.958 18.5001 42.2863 18.5 41.458H17.2002C16.0802 41.458 15.5196 41.4582 15.0918 41.2402C14.7156 41.0485 14.4095 40.7424 14.2178 40.3662C13.9998 39.9384 14 39.3778 14 38.2578V27.5811C14 26.4611 13.9999 25.9005 14.2178 25.4727C14.4095 25.0965 14.7156 24.7903 15.0918 24.5986C15.5196 24.3806 16.0801 24.3809 17.2002 24.3809H18.5V15.8428C18.5 15.0143 19.1716 14.3428 20 14.3428Z" fill="#003129"/>
            <path d="M34 2.95801C34.8284 2.95801 35.5 3.62958 35.5 4.45801V6.35547H36.7998C37.9199 6.35547 38.4804 6.35526 38.9082 6.57324C39.2844 6.76498 39.5905 7.07104 39.7822 7.44727C40.0001 7.87507 40 8.43572 40 9.55566V27.8223C40 28.9418 40 29.502 39.7822 29.9297C39.5905 30.3058 39.2843 30.612 38.9082 30.8037C38.4804 31.0217 37.9198 31.0225 36.7998 31.0225H35.5V41.458C35.4999 42.2863 34.8284 42.958 34 42.958C33.1716 42.958 32.5001 42.2863 32.5 41.458V31.0225H31.2002C30.0802 31.0225 29.5196 31.0217 29.0918 30.8037C28.7157 30.612 28.4095 30.3058 28.2178 29.9297C28 29.502 28 28.9418 28 27.8223V9.55566C28 8.43572 27.9999 7.87507 28.2178 7.44727C28.4095 7.07104 28.7156 6.76498 29.0918 6.57324C29.5196 6.35526 30.0801 6.35547 31.2002 6.35547H32.5V4.45801C32.5 3.62958 33.1716 2.95801 34 2.95801Z" fill="#003129"/>
            <path d="M68.5654 33.7607L83.3408 5.8291H91.1855V42.584H83.3408V23.7412L73.2998 42.584H63.8857L53.8447 23.835V42.584H46V5.8291H53.8447L68.5654 33.7607Z" fill="#003129"/>
          </svg></a>
          {!isMobile && <span style={{ position: "relative", top: 6 }}>– manage <span style={{ fontWeight: 700 }}>your</span> money, with smarts</span>}
        </span>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12 }}>
          {session ? (
            <>
              {!isMobile && <Text size="xs" c="#bbb">{session.user.email}</Text>}
              {subscribed && (
                <button style={{ fontSize: 12, color: "#bbb", background: "none", border: "none", cursor: "pointer", padding: 0 }} onClick={async () => {
                  const res = await fetch("/api/stripe/portal", { method: "POST", headers: { "Authorization": `Bearer ${session.sessionToken}` } });
                  const { url } = await res.json();
                  if (url) window.location.href = url;
                }}>Manage subscription</button>
              )}
              <Button variant="outline" size="xs" radius="xl" styles={{ root: { borderColor: "#003129", color: "#00665A" } }} onClick={signOut}>Sign out</Button>
            </>
          ) : (
            <Button variant="filled" size="xs" radius="xl" onClick={() => setAuthModalOpen(true)}>{isMobile ? "Log in" : "Save your data by logging in – no subscription required"}</Button>
          )}
        </div>
        </div>
      </header>

      {/* Mobile tab bar */}
      {isMobile && (
        <div style={{ flexShrink: 0, borderBottom: "0.5px solid rgba(0,0,0,0.08)", background: "white", overflowX: "auto", minWidth: 0 }}>
          <div style={{ display: "flex", gap: 6, padding: "8px 12px", minWidth: "max-content" }}>
            {NAV_ITEMS.map(item => {
              const isActive = activePage === item.key;
              return (
                <button key={item.key} onClick={() => setActivePage(item.key)} style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "5px 12px", borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: "pointer",
                  background: isActive ? "#CAFFFB" : "transparent",
                  color: "#003129",
                  border: `0.5px solid ${isActive ? "#CAFFFB" : "#00665A"}`,
                }}>
                  <i className={`ti ${item.icon}`} style={{ fontSize: 14 }} />
                  {item.label}
                </button>
              );
            })}
            <button onClick={() => { setFeedbackOpen(true); setFeedbackSent(false); setFeedbackText(""); setFeedbackError(null); }} style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "5px 12px", borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: "pointer",
              background: "transparent", color: "#003129",
              border: "0.5px solid #00665A",
            }}>
              <i className="ti ti-message" style={{ fontSize: 14 }} />
              Feedback
            </button>
            <Button variant="subtle" size="xs" radius="xl" styles={{ root: { marginLeft: -3 }, label: { color: "#00665A", fontWeight: 400 } }} onClick={() => setCookieBanner(true)}>
              Manage cookies
            </Button>
          </div>
        </div>
      )}

      {/* Body */}
      <div style={{ display: "flex", flex: 1, overflow: "hidden", paddingBottom: cookieBanner ? 64 : 0 }}>

        {/* Side nav — hidden on mobile, replaced by tab bar above */}
        <nav style={{
          width: isMobile ? 0 : 200,
          flexShrink: 0,
          background: "white",
          borderRight: isMobile ? "none" : "0.5px solid rgba(0,0,0,0.1)",
          display: isMobile ? "none" : "flex",
          flexDirection: "column",
          padding: "20px 0",
          overflow: "hidden",
        }}>
          {NAV_ITEMS.map((item) => (
            isMobile ? (
              <NavLink
                key={item.key}
                aria-label={item.label}
                leftSection={<i className={`ti ${item.icon}`} style={{ fontSize: 20 }} aria-hidden="true" />}
                active={activePage === item.key}
                onClick={() => setActivePage(item.key)}
                color="brand"
                styles={{
                  root: { borderRadius: 8, margin: "2px 4px", padding: "10px 0", justifyContent: "center" },
                  section: { margin: 0 },
                }}
              />
            ) : (
              <AppTooltip key={item.key} label={item.tooltip} enabled={tooltipsEnabled} position="right">
                <NavLink
                  label={item.label}
                  aria-label={item.label}
                  leftSection={<i className={`ti ${item.icon}`} style={{ fontSize: 18 }} aria-hidden="true" />}
                  active={activePage === item.key}
                  onClick={() => setActivePage(item.key)}
                  color="gray"
                  style={{ "--nl-color": "#444" }}
                  styles={{
                    root: { borderRadius: 8, margin: "2px 12px" },
                    label: { fontSize: 14, fontWeight: activePage === item.key ? 600 : 400 },
                  }}
                />
              </AppTooltip>
            )
          ))}
          <div style={{ marginTop: "auto" }}>
            {isMobile ? (
              <NavLink
                aria-label="Feedback"
                leftSection={<i className="ti ti-message" style={{ fontSize: 20 }} aria-hidden="true" />}
                onClick={() => { setFeedbackOpen(true); setFeedbackSent(false); setFeedbackText(""); setFeedbackError(null); }}
                color="gray"
                styles={{
                  root: { borderRadius: 8, margin: "2px 4px", padding: "10px 0", justifyContent: "center" },
                  section: { margin: 0 },
                }}
              />
            ) : (
              <>
                <NavLink
                  label="Tooltips"
                  leftSection={<i className="ti ti-info-circle" style={{ fontSize: 18 }} aria-hidden="true" />}
                  onClick={() => setTooltipsEnabled(prev => { const next = !prev; localStorage.setItem("tooltipsEnabled", next); return next; })}
                  color="gray"
                  style={{ "--nl-color": "#444" }}
                  styles={{
                    root: { borderRadius: 8, margin: "2px 12px" },
                    label: { fontSize: 14, fontWeight: 400 },
                  }}
                  rightSection={
                    <div onClick={e => e.stopPropagation()} style={{ marginRight: 12 }}>
                      <Switch
                        size="md"
                        checked={tooltipsEnabled}
                        onChange={() => setTooltipsEnabled(prev => { const next = !prev; localStorage.setItem("tooltipsEnabled", next); return next; })}
                      />
                    </div>
                  }
                />
                <NavLink
                  label="Feedback"
                  leftSection={<i className="ti ti-message" style={{ fontSize: 18 }} aria-hidden="true" />}
                  onClick={() => { setFeedbackOpen(true); setFeedbackSent(false); setFeedbackText(""); setFeedbackError(null); }}
                  color="gray"
                  style={{ "--nl-color": "#444" }}
                  styles={{
                    root: { borderRadius: 8, margin: "2px 12px" },
                    label: { fontSize: 14, fontWeight: 400 },
                  }}
                />
                <NavLink
                  label="Manage cookies"
                  leftSection={<i className="ti ti-cookie" style={{ fontSize: 18 }} aria-hidden="true" />}
                  onClick={() => setCookieBanner(true)}
                  color="gray"
                  style={{ "--nl-color": "#444" }}
                  styles={{
                    root: { borderRadius: 8, margin: "2px 12px" },
                    label: { fontSize: 14, fontWeight: 400 },
                  }}
                />
              </>
            )}
          </div>
        </nav>

        {/* Page content */}
        <main style={{ flex: 1, overflowX: "hidden", overflowY: isMobile ? "auto" : "hidden", display: "flex", minWidth: 0 }}>

          {activePage === "design" && (
            <div style={{ flex: 1, display: "flex", flexDirection: isMobile ? "column" : "row", overflow: isMobile ? "visible" : "hidden" }}>

              {/* Left column — investment amount */}
              <div style={{
                width: isMobile ? "100%" : 200,
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                padding: isMobile ? "16px 16px 20px" : "22px 24px",
                borderBottom: isMobile ? "0.5px solid rgba(0,0,0,0.07)" : "none",
              }}>
                <InvestmentAmountInput currency={currency} setCurrency={setCurrency} portfolioSize={portfolioSize} setPortfolioSize={setPortfolioSize} tooltipsEnabled={tooltipsEnabled} isMobile={isMobile} />

                <input
                  ref={csvImportInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleCsvFileSelected}
                  style={{ display: "none" }}
                />
                <AppTooltip label="All data is processed locally on your machine & we only read investment names and allocations – however, please avoid files where the name column includes account numbers or similar personal details" enabled={tooltipsEnabled} position="right">
                  <Button
                    variant="default" size="xs" radius="xl"
                    styles={{ root: { marginTop: 16 }, label: { color: "#00665A" } }}
                    onClick={handleImportCsvClick}
                    disabled={locked}
                  >
                    + Import portfolio CSV
                  </Button>
                </AppTooltip>
              </div>

              {/* Centre column — donut chart */}
              <div style={{
                flex: 3,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "24px 0",
                overflow: isMobile ? "visible" : "hidden",
              }}>

                <TextInput
                  variant="unstyled"
                  value={portfolioName}
                  onChange={(e) => setPortfolioName(e.target.value)}
                  placeholder="Name your test portfolio"
                  w={240}
                  style={{ marginBottom: 60, borderBottom: "0.5px solid rgba(0,0,0,0.1)" }}
                  styles={{
                    input: {
                      fontSize: 14,
                      color: "#444",
                      letterSpacing: "0.04em",
                      textAlign: "center",
                      paddingBottom: 6,
                      background: "transparent",
                    },
                  }}
                />

                <div style={{ position: "relative", width: svgSize, height: svgSize, marginTop: 20, marginBottom: 20 }}>
                <svg
                  ref={svgRef}
                  width={svgSize}
                  height={svgSize}
                  viewBox="0 0 420 420"
                  style={{ overflow: "visible", display: "block" }}
                >
                  {segmentData.map((seg, i) => {
                    const isHovered  = hovered === i;
                    const midAngle   = seg.mid % 360;
                    const labelPos   = polarToCartesian(CX, CY, LABEL_R, midAngle);
                    const handlePos  = polarToCartesian(CX, CY, HANDLE_R, boundaries[i]);
                    const isDragging = draggingHandle === i;
                    const isEditing  = editingIndex === i;

                    const nameY  = labelPos.y - 16;
                    const sizeY  = labelPos.y + 4;
                    const valueY = labelPos.y + 23;

                    return (
                      <g key={i}>
                        <path
                          d={arcPath(CX, CY, BASE_INNER, OUTER_R, seg.start, seg.end)}
                          fill={isHovered ? "#8ECFC5" : "#A6E6DC"}
                          style={{ transition: draggingHandle !== null ? "none" : "fill 0.2s ease", cursor: "default" }}
                          onMouseEnter={() => setHovered(i)}
                          onMouseLeave={() => setHovered(null)}
                        />

                        {editingLabelIndex === i ? (
                          <foreignObject x={labelPos.x - 36} y={nameY - 10} width={72} height={20}>
                            <input
                              ref={labelInputRef}
                              type="text"
                              value={editLabelValue}
                              onChange={(e) => setEditLabelValue(e.target.value)}
                              onBlur={() => commitLabelEdit(i)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") commitLabelEdit(i);
                                if (e.key === "Escape") { setEditingLabelIndex(null); setEditLabelValue(""); }
                              }}
                              style={{
                                width: "100%", height: "100%", textAlign: "center",
                                fontSize: 14, border: "1px solid #888", borderRadius: 4,
                                background: "white", color: "#333", padding: 0, outline: "none",
                                boxSizing: "border-box",
                              }}
                            />
                          </foreignObject>
                        ) : (
                          <g
                            onMouseEnter={() => setHoveredLabel("name-" + i)}
                            onMouseLeave={() => setHoveredLabel(null)}
                            onClick={(e) => { if (locked) return; e.stopPropagation(); setEditingLabelIndex(i); setEditLabelValue(segmentLabels[i] || "Label"); }}
                            style={{ cursor: "text" }}
                          >
                            <rect x={labelPos.x - 28} y={nameY - 9} width={56} height={18} rx={3}
                              fill="none" stroke={hoveredLabel === "name-" + i ? "#4a90d9" : "none"} strokeWidth={1.5} />
                            <text x={labelPos.x} y={nameY} textAnchor="middle" dominantBaseline="middle"
                              style={{ fontSize: 14, fontWeight: 400, fill: "#444", cursor: "text", fontFamily: "Inter, system-ui, sans-serif" }}>
                              {segmentLabels[i] || "Label"}
                            </text>
                          </g>
                        )}

                        {isEditing ? (
                          <foreignObject x={labelPos.x - 22} y={sizeY - 10} width={44} height={20}>
                            <input
                              ref={inputRef}
                              type="number" min="0.1" max="99.9" step="0.1"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onBlur={() => commitEdit(i)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") commitEdit(i);
                                if (e.key === "Escape") { setEditingIndex(null); setEditValue(""); }
                              }}
                              style={{
                                width: "100%", height: "100%", textAlign: "center",
                                fontSize: 14, border: "1px solid #888", borderRadius: 4,
                                background: "white", color: "#333", padding: 0, outline: "none",
                                boxSizing: "border-box", MozAppearance: "textfield",
                              }}
                            />
                          </foreignObject>
                        ) : (
                          <g
                            onMouseEnter={() => setHoveredLabel("size-" + i)}
                            onMouseLeave={() => setHoveredLabel(null)}
                            onClick={(e) => { if (locked) return; e.stopPropagation(); setEditingIndex(i); setEditValue(String(pcts[i])); }}
                            style={{ cursor: "text" }}
                          >
                            <rect x={labelPos.x - 20} y={sizeY - 9} width={40} height={18} rx={3}
                              fill="none" stroke={hoveredLabel === "size-" + i ? "#4a90d9" : "none"} strokeWidth={1.5} />
                            <text x={labelPos.x} y={sizeY} textAnchor="middle" dominantBaseline="middle"
                              style={{ fontSize: 14, fontWeight: 500, fill: "#444", cursor: "text", fontFamily: "Inter, system-ui, sans-serif" }}>
                              {pcts[i]}%
                            </text>
                          </g>
                        )}

                        {portfolioSize && (
                          <text x={labelPos.x} y={valueY} textAnchor="middle" dominantBaseline="middle"
                            style={{ fontSize: 13, fontWeight: 400, fill: "#bbb", pointerEvents: "none", fontFamily: "Inter, system-ui, sans-serif" }}>
                            {currency}{Math.round((parseFloat(portfolioSize) || 0) * pcts[i] / 100).toLocaleString()}
                          </text>
                        )}

                        <circle
                          cx={handlePos.x} cy={handlePos.y}
                          r={isDragging ? 7 : 5}
                          fill={isDragging ? "#555" : "#888"}
                          stroke="white" strokeWidth={2}
                          style={{ pointerEvents: "none", transition: "r 0.1s, fill 0.1s" }}
                        />
                        <circle
                          cx={handlePos.x} cy={handlePos.y}
                          r={30}
                          fill="transparent"
                          style={{ cursor: "grab" }}
                          onMouseDown={(e) => {
                            if (locked) return;
                            e.preventDefault();
                            setDraggingHandle(i);
                            document.body.style.cursor = "grabbing";
                          }}
                          onTouchStart={(e) => {
                            if (locked) return;
                            if (e.touches.length !== 1) return;
                            e.preventDefault();
                            setDraggingHandle(i);
                          }}
                        />
                      </g>
                    );
                  })}

                  {/* Lock icons — rendered as absolutely-positioned HTML outside SVG to avoid Safari foreignObject issues */}

                </svg>

                {/* Lock toggle — absolutely positioned over SVG centre */}
                <div style={{ position: "absolute", top: `${(CY - 90) / 420 * 100}%`, left: "50%", transform: "translateX(-50%)", display: "flex", alignItems: "center", gap: 6, pointerEvents: "auto" }}>
                  <i className="ti ti-lock-open" style={{ fontSize: 28, color: locked ? "#bbb" : "#003129" }} />
                  <Switch checked={locked} onChange={(e) => setLocked(e.currentTarget.checked)} size="md" />
                  <i className="ti ti-lock" style={{ fontSize: 28, color: locked ? "#003129" : "#bbb" }} />
                </div>

                {/* Get insight button — absolutely positioned over SVG centre */}
                <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", pointerEvents: "auto" }}>
                  {insightLoading ? (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                      <Loader size="sm" color="blue" />
                      <span style={{ fontSize: 11, color: "#888", fontFamily: "Inter, system-ui, sans-serif" }}>{insightElapsed}s</span>
                    </div>
                  ) : (
                    <Button variant="filled" size="sm" radius="xl"
                      onClick={!subscribed && insightCount >= INSIGHT_LIMIT ? openCheckout : handleGetInsightClick}>
                      {!subscribed && insightCount >= INSIGHT_LIMIT ? "Subscribe" : "Get insight"}
                    </Button>
                  )}
                </div>

                </div>{/* end SVG wrapper */}

                {/* Constituents controls */}
                <div style={{ display: "flex", alignItems: "center", gap: 20, marginTop: 60 }}>
                  <ActionIcon
                    variant="default"
                    color="gray"
                    size="lg"
                    radius="xl"
                    onClick={removeSegment}
                    disabled={n <= 1 || locked}
                    aria-label="Remove segment"
                  >
                    −
                  </ActionIcon>
                  <span style={{ fontSize: 14, color: "#666", minWidth: 90, textAlign: "center" }}>
                    {n} Component{n !== 1 ? "s" : ""}
                  </span>
                  <ActionIcon
                    variant="default"
                    color="gray"
                    size="lg"
                    radius="xl"
                    onClick={addSegment}
                    disabled={locked}
                    aria-label="Add segment"
                  >
                    +
                  </ActionIcon>
                </div>

              </div>

              {/* Right column — AI insight panel */}
              <div ref={insightPanelRef} style={{
                flex: isMobile ? "none" : 2,
                borderLeft: isMobile ? "none" : "0.5px solid rgba(0,0,0,0.07)",
                borderTop: isMobile ? "0.5px solid rgba(0,0,0,0.07)" : "none",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                maxHeight: isMobile ? 400 : "none",
              }}>

                {/* Scrollable content */}
                <div style={{ flex: 1, overflowY: "auto", padding: "40px 24px", paddingBottom: 80 }}>
                  <Text size="sm" c="#444" style={{ letterSpacing: "0.06em", marginBottom: 16 }}>
                    Portfolio insight
                  </Text>

                  {!subscribed && insightCount >= INSIGHT_LIMIT ? (
                    <Text size="sm" c="#bbb" style={{ lineHeight: 1.7 }}>
                      You've used your {INSIGHT_LIMIT} free insights. <button onClick={openCheckout} style={{ background: "none", border: "none", padding: 0, color: "#003129", fontWeight: 600, cursor: "pointer", fontSize: "inherit" }}>Subscribe to continue.</button>
                    </Text>
                  ) : insightLoading && !insight ? (
                    <Text size="sm" c="#bbb" style={{ lineHeight: 1.7 }}>
                      Analysing your portfolio…
                    </Text>
                  ) : insightError ? (
                    <Text size="sm" c="red" style={{ lineHeight: 1.7 }}>
                      {insightError}
                    </Text>
                  ) : insight ? (
                    <>
                      <div style={{ fontSize: 14, color: "#444", lineHeight: 1.8 }}>
                        <ReactMarkdown
                          components={{
                            h2: ({ children }) => {
                              const text = Array.isArray(children) ? children.join("") : String(children || "");
                              const sc = text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
                              return <h2 style={{ fontSize: 28, fontWeight: 600, color: "#1a1a1a", marginTop: 28, marginBottom: 10, lineHeight: 1.2 }}>{sc}</h2>;
                            },
                            p: ({ children }) => {
                              const text = Array.isArray(children) ? children.join("") : String(children || "");
                              const match = text.match(/^Correlation level:\s*(High|Medium|Low)$/i);
                              if (match) {
                                const level = match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase();
                                const colors = { High: { bg: "#fdecea", text: "#c0392b" }, Medium: { bg: "#fef6e4", text: "#b7770d" }, Low: { bg: "#eaf6ee", text: "#2e7d4f" } };
                                const c = colors[level] || colors.Medium;
                                return (
                                  <p style={{ marginBottom: 16 }}>
                                    <span style={{ display: "inline-block", background: c.bg, color: c.text, fontWeight: 600, fontSize: 12, letterSpacing: "0.04em", padding: "4px 10px", borderRadius: 4 }}>
                                      {level}
                                    </span>
                                  </p>
                                );
                              }
                              return <p style={{ marginBottom: 16 }}>{children}</p>;
                            },
                            strong: ({ children }) => <strong style={{ fontWeight: 600, color: "#222" }}>{children}</strong>,
                            em:     ({ children }) => <em style={{ fontStyle: "italic" }}>{children}</em>,
                            ul:     ({ children }) => <ul style={{ paddingLeft: 20, marginBottom: 16, listStyleType: "disc" }}>{children}</ul>,
                            li:     ({ children }) => <li style={{ marginBottom: 6, display: "list-item" }}>{children}</li>,
                          }}
                        >
                          {insight}
                        </ReactMarkdown>
                      </div>
                      <Text size="xs" c="#bbb" style={{ marginTop: 8, lineHeight: 1.6 }}>
                        ⚠️ AI-generated and illustrative only. Not financial advice.
                      </Text>
                    </>
                  ) : (
                    <>
                      <Text size="sm" c="#bbb" style={{ lineHeight: 1.7 }}>
                        Create your hypothetical portfolio, and hit 'Get insight' to explore potential hidden similarity & underlying theses – before you invest.
                      </Text>
                      <div style={{ marginTop: 32 }}>
                        {[
                          { title: "Hidden similarity",     body: "Spot potential similarity between investments that look different, but may move together under the same market conditions." },
                          { title: "Underlying thesis",     body: "Understand possible investment philosophies or macro views that your portfolio may imply – even if you haven't consciously planned one." },
                          { title: "Strengths & weaknesses", body: "Get a balanced view of what may / not work in your portfolio, and where the risks may lie." },
                        ].map((section) => (
                          <div key={section.title} style={{ marginBottom: 20 }}>
                            <Text size="sm" c="#999" style={{ fontWeight: 600, marginBottom: 4 }}>
                              {section.title}:
                            </Text>
                            <Text size="sm" c="#bbb" style={{ lineHeight: 1.7 }}>
                              {section.body}
                            </Text>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Disclaimer banner — only shown after insight has been requested */}
                {(insight || insightLoading || insightError) && <Paper
                  radius={0}
                  style={{
                    flexShrink: 0,
                    background: "#1a1a1a",
                    padding: "14px 24px",
                  }}
                >
                  <Text size="xs" c="white" style={{ lineHeight: 1.6, opacity: 0.75 }}>
                    This content is for information purposes only and is not investment advice. Insights are AI generated and can be inaccurate - do your own research and double-check responses.
                  </Text>
                  <Text size="xs" c="white" style={{ lineHeight: 1.6, opacity: 0.75, marginTop: 8 }}>
                    If you choose to invest your capital is at risk – the value of any financial product inc shares, ETFs & ETCs can fall as well as rise; which could mean getting back less than you originally put in – past performance is not an indication of future results.
                  </Text>
                </Paper>}

              </div>

            </div>
          )}

          {activePage === "rebalance" && (
            <div style={{ flex: 1, display: "flex", flexDirection: isMobile ? "column" : "row", overflow: isMobile ? "visible" : "hidden" }}>

              {/* Left column — investment amount (read-only) */}
              <div style={{
                width: isMobile ? "100%" : 200,
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                padding: isMobile ? "16px 16px 20px" : "22px 24px",
                borderBottom: isMobile ? "0.5px solid rgba(0,0,0,0.07)" : "none",
              }}>
                <InvestmentAmountInput currency={currency} setCurrency={setCurrency} portfolioSize={portfolioSize} setPortfolioSize={setPortfolioSize} tooltipsEnabled={tooltipsEnabled} isMobile={isMobile} />
              </div>

              {/* Centre column — chart */}
              <div style={{
                flex: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "24px 0",
                overflow: "visible",
              }}>
              <svg
                width={svgSize}
                height={svgSize}
                viewBox="0 0 420 420"
                style={{ overflow: "visible" }}
              >
                {segmentData.map((seg, i) => {
                  const outerR      = rebalOuterR[i] ?? REBAL_OUTER_BASE;
                  const midAngle    = seg.mid % 360;
                  const labelPos    = polarToCartesian(REBAL_CX, REBAL_CY, REBAL_LABEL_R, midAngle);
                  const arcEdgePos  = polarToCartesian(REBAL_CX, REBAL_CY, outerR + 8, midAngle);
                  const inputAnchor = polarToCartesian(REBAL_CX, REBAL_CY, REBAL_INPUT_R, midAngle);

                  const targetVal  = rebalPortfolioVal > 0 ? Math.round(rebalPortfolioVal * pcts[i] / 100) : null;
                  const currentVal = rebalValues[i] !== "" ? parseFloat(rebalValues[i]) || 0 : null;
                  const delta      = targetVal !== null && currentVal !== null
                    ? Math.round(currentVal - targetVal) : null;

                  const nameY  = labelPos.y - 16;
                  const targY  = labelPos.y + 4;
                  const deltaY = labelPos.y + 24;

                  const cosA   = Math.cos(((midAngle - 90) * Math.PI) / 180);
                  const inputX = cosA > 0.1  ? inputAnchor.x
                               : cosA < -0.1 ? inputAnchor.x - 140
                               :               inputAnchor.x - 70;

                  return (
                    <g key={i}>
                      <path
                        d={arcPath(REBAL_CX, REBAL_CY, REBAL_INNER, REBAL_OUTER_BASE, seg.start, seg.end)}
                        fill="#A6E6DC"
                        opacity={0.6}
                      />
                      <path
                        d={arcPath(REBAL_CX, REBAL_CY, REBAL_INNER, outerR, seg.start, seg.end)}
                        fill="#A6E6DC"
                        opacity={0.6}
                        style={{ mixBlendMode: "multiply" }}
                      />

                      <text x={labelPos.x} y={nameY} textAnchor="middle" dominantBaseline="middle"
                        style={{ fontSize: 14, fill: "#444", fontFamily: "Inter, system-ui, sans-serif" }}>
                        {segmentLabels[i] || "Label"}
                      </text>

                      {targetVal !== null && (
                        <text x={labelPos.x} y={targY} textAnchor="middle" dominantBaseline="middle"
                          style={{ fontSize: 13, fill: "#bbb", fontFamily: "Inter, system-ui, sans-serif" }}>
                          {currency}{targetVal.toLocaleString()}
                        </text>
                      )}

                      {delta !== null && (
                        <text x={labelPos.x} y={deltaY} textAnchor="middle" dominantBaseline="middle"
                          style={{ fontSize: 13, fontWeight: 600, fill: delta >= 0 ? "#4a9d5f" : "#d94f4f", fontFamily: "Inter, system-ui, sans-serif" }}>
                          {delta >= 0
                            ? `+${currency}${Math.abs(delta).toLocaleString()}`
                            : `−${currency}${Math.abs(delta).toLocaleString()}`}
                        </text>
                      )}

                      {!isMobile && <line
                        x1={arcEdgePos.x} y1={arcEdgePos.y}
                        x2={inputAnchor.x} y2={inputAnchor.y}
                        stroke="rgba(0,0,0,0.1)" strokeWidth={1}
                      />}

                      {!isMobile && <foreignObject x={inputX} y={inputAnchor.y - 13} width={140} height={26}>
                        <AppTooltip
                          label="Enter the current value of your investment & hit 'Rebalance' – the rebalance engine will auto-calculate the trades required to bring your portfolio back in line with your ideal component ratios"
                          enabled={tooltipsEnabled}
                          position="right"
                        >
                        <div style={{ position: "relative", width: "100%", height: "100%" }}>
                          <span style={{
                            position: "absolute", left: 6, top: "50%", transform: "translateY(-50%)",
                            fontSize: 12, color: "#555", fontFamily: "Inter, system-ui, sans-serif",
                            pointerEvents: "none", userSelect: "none",
                          }}>
                            {currency}
                          </span>
                          <input
                            type="number"
                            min="0"
                            value={rebalValues[i]}
                            onChange={(e) => handleRebalValue(i, e.target.value)}
                            placeholder="Enter current value"
                            style={{
                              width: "100%", height: "100%", fontSize: 12,
                              border: "0.5px solid rgba(0,0,0,0.2)", borderRadius: 4,
                              padding: "0 6px 0 16px", background: "white", color: "#333",
                              outline: "none", boxSizing: "border-box",
                              fontFamily: "Inter, system-ui, sans-serif",
                              MozAppearance: "textfield",
                            }}
                          />
                        </div>
                        </AppTooltip>
                      </foreignObject>}

                    </g>
                  );
                })}

                {/* Balance portfolio CTA */}
                {!isMobile && <foreignObject x={REBAL_CX - 85} y={REBAL_CY - 18} width={170} height={36}>
                  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                    {!subscribed && rebalCount >= REBAL_LIMIT
                      ? <Button variant="filled" size="sm" radius="xl" fullWidth onClick={openCheckout}>Subscribe</Button>
                      : <Button
                          variant="filled" size="sm" radius="xl" fullWidth
                          disabled={
                            !rebalValues.some(v => v !== "") &&
                            rebalOuterR.every(r => r === REBAL_OUTER_BASE) &&
                            !(hasEverRebalanced && rebalTrades === null)
                          }
                          onClick={handleAutoRebalance}
                        >
                          Balance portfolio
                        </Button>
                    }
                  </div>
                </foreignObject>}

                {/* Rebalance error message */}
                {!isMobile && rebalError && (
                  <foreignObject x={REBAL_CX - 120} y={REBAL_CY + 26} width={240} height={40}>
                    <div style={{ textAlign: "center", fontSize: 11, color: "#f59f00", lineHeight: 1.4 }}>
                      {rebalError}
                    </div>
                  </foreignObject>
                )}
              </svg>
              </div>

              {/* Mobile: current value inputs below chart */}
              {isMobile && (
                <div style={{ padding: "16px", borderTop: "0.5px solid rgba(0,0,0,0.07)" }}>
                  {segmentLabels.map((label, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                      <Text size="sm" c="#444" style={{ width: 90, flexShrink: 0, fontWeight: 500 }}>{label}</Text>
                      <div style={{ position: "relative", flex: 1 }}>
                        <span style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "#555", pointerEvents: "none" }}>{currency}</span>
                        <input
                          type="number" min="0"
                          value={rebalValues[i]}
                          onChange={(e) => handleRebalValue(i, e.target.value)}
                          placeholder="Current value"
                          style={{ width: "100%", height: 36, paddingLeft: 22, paddingRight: 8, border: "0.5px solid rgba(0,0,0,0.2)", borderRadius: 6, fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "Inter, system-ui, sans-serif" }}
                        />
                      </div>
                    </div>
                  ))}
                  <Button fullWidth variant="filled" size="sm" radius="xl" style={{ marginTop: 4 }}
                    disabled={!rebalValues.some(v => v !== "") && rebalOuterR.every(r => r === REBAL_OUTER_BASE) && !(hasEverRebalanced && rebalTrades === null)}
                    onClick={!subscribed && rebalCount >= REBAL_LIMIT ? openCheckout : handleAutoRebalance}>
                    {rebalCount >= REBAL_LIMIT ? "Subscribe" : "Balance portfolio"}
                  </Button>
                  {rebalError && <Text size="xs" style={{ color: "#f59f00", marginTop: 8, textAlign: "center" }}>{rebalError}</Text>}
                </div>
              )}

              {/* Right column — Rebalance trades */}
              <div style={{
                flex: isMobile ? "none" : 2,
                borderLeft: isMobile ? "none" : "0.5px solid rgba(0,0,0,0.07)",
                borderTop: isMobile ? "0.5px solid rgba(0,0,0,0.07)" : "none",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                maxHeight: isMobile ? 400 : "none",
              }}>
                <div style={{ flex: 1, overflowY: "auto", padding: "40px 24px" }}>
                  <Text size="sm" c="#444" style={{ letterSpacing: "0.06em", marginBottom: 12 }}>
                    Rebalance trades
                  </Text>
                  <Text size="sm" c="#888" style={{ lineHeight: 1.6, marginBottom: 24 }}>
                    You could decide to modify your investments in order to regain the initial component weighting that you have specified within the portfolio designer
                  </Text>
                  {rebalTrades !== null ? (
                    rebalTrades.map(({ label, trade }, i) => (
                      <div key={i} style={{ marginBottom: 20 }}>
                        <Text size="sm" c="#444" style={{ fontWeight: 500, marginBottom: 2 }}>
                          {label}
                        </Text>
                        <Text size="sm" style={{ fontWeight: 600, color: trade > 0 ? "#4a9d5f" : trade < 0 ? "#d94f4f" : "#888" }}>
                          {trade > 0
                            ? `Currently smaller than your desired weighting by ${currency}${trade.toLocaleString()}`
                            : trade < 0
                            ? `Currently larger than your desired weighting by ${currency}${Math.abs(trade).toLocaleString()}`
                            : "No change"}
                        </Text>
                      </div>
                    ))
                  ) : (
                    <Text size="sm" c="#bbb" style={{ lineHeight: 1.7 }}>
                      Enter current values and hit 'Balance portfolio' to see suggested trades.
                    </Text>
                  )}
                </div>
                {rebalTrades !== null && (
                  <Paper radius={0} style={{ flexShrink: 0, background: "#1a1a1a", padding: "14px 24px" }}>
                    <Text size="xs" c="white" style={{ lineHeight: 1.6, opacity: 0.75 }}>
                      This content does not constitute investment advice – it is an outline of possible actions in order to resize individual investments within an investment portfolio, to your chosen portfolio structure / weighting of investments.
                    </Text>
                  </Paper>
                )}
              </div>

            </div>
          )}

          {activePage === "track" && (() => {
            // Show sample chart/ledger data only for the seeded "Example account" (id 1)
            // while it has no real snapshots — purely a render-layer overlay, never
            // written to trackTxs/trackSnaps state, so it can never reach /api/sync.
            // Switching to any other account (always id >= 2) instantly reveals the
            // genuine empty state.
            const isSampleTrackActive = activeTrackAcc === SAMPLE_TRACK_ACCOUNT_ID
              && !trackSnaps.some(s => s.accountId === SAMPLE_TRACK_ACCOUNT_ID);
            const effTrackTxs = isSampleTrackActive ? [...trackTxs, ...SAMPLE_TRACK_TXS] : trackTxs;
            const effTrackSnaps = isSampleTrackActive ? [...trackSnaps, ...SAMPLE_TRACK_SNAPS] : trackSnaps;

            const getDeposits = (accId, upTo = null) =>
              effTrackTxs.filter(t =>
                ((t.type === "deposit" && t.accountId === accId) || (t.type === "transfer" && t.toAccountId === accId)) &&
                (upTo === null || t.date <= upTo)
              ).reduce((s, t) => s + t.amount, 0);

            const getPureWithdrawals = (accId, upTo = null) =>
              effTrackTxs.filter(t =>
                t.type === "withdrawal" && t.accountId === accId &&
                (upTo === null || t.date <= upTo)
              ).reduce((s, t) => s + t.amount, 0);

            const getTransfersOut = (accId, upTo = null) =>
              effTrackTxs.filter(t =>
                t.type === "transfer" && t.accountId === accId &&
                (upTo === null || t.date <= upTo)
              ).reduce((s, t) => s + t.amount, 0);

            const getLatestSnap = (accId, upTo = null) => {
              const snaps = effTrackSnaps
                .filter(s => s.accountId === accId && (upTo === null || s.date <= upTo))
                .sort((a, b) => b.date.localeCompare(a.date));
              return snaps[0]?.value ?? null;
            };

            const getAccPerf = (accId, upTo = null) => {
              const v = getLatestSnap(accId, upTo);
              return v === null ? null : v - getDeposits(accId, upTo) + getTransfersOut(accId, upTo);
            };

            const getGhostPerf = (accId, upTo = null) => {
              const v = getLatestSnap(accId, upTo);
              return v === null ? null : v + getPureWithdrawals(accId, upTo) - getDeposits(accId, upTo) + getTransfersOut(accId, upTo);
            };

            const totalPerfVal = activeTrackAcc !== null
              ? getAccPerf(activeTrackAcc)
              : (() => {
                  const ps = trackAccounts.map(a => getAccPerf(a.id)).filter(p => p !== null);
                  return ps.length ? ps.reduce((s, p) => s + p, 0) : null;
                })();
            const ghostPerfVal = activeTrackAcc !== null
              ? getGhostPerf(activeTrackAcc)
              : (() => {
                  const ps = trackAccounts.map(a => getGhostPerf(a.id)).filter(p => p !== null);
                  return ps.length ? ps.reduce((s, p) => s + p, 0) : null;
                })();

            const chartData = (() => {
              if (activeTrackAcc !== null) {
                return effTrackSnaps
                  .filter(s => s.accountId === activeTrackAcc)
                  .sort((a, b) => a.date.localeCompare(b.date))
                  .map(s => ({ date: s.date, value: s.value }));
              }
              const dates = [...new Set(effTrackSnaps.map(s => s.date))].sort();
              return dates.map(date => {
                const ps = trackAccounts.map(a => getLatestSnap(a.id, date)).filter(v => v !== null);
                return ps.length ? { date, value: ps.reduce((s, p) => s + p, 0) } : null;
              }).filter(Boolean);
            })();

            const ledgerRows = (activeTrackAcc === null
              ? [
                  ...effTrackTxs.map(t => ({
                    key: `tx-${t.id}`, txId: t.id, snapId: null,
                    kind: t.type === "transfer" ? "transfer-out" : t.type,
                    amount: t.amount, date: t.date, otherAccId: t.toAccountId, accountId: t.accountId, isSample: t.id < 0,
                  })),
                  ...effTrackSnaps.map(s => ({
                    key: `snap-${s.id}`, txId: null, snapId: s.id,
                    kind: "snapshot", amount: s.value, date: s.date, otherAccId: null, accountId: s.accountId, isSample: s.id < 0,
                  })),
                ]
              : [
                  ...effTrackTxs.filter(t => t.accountId === activeTrackAcc).map(t => ({
                    key: `tx-${t.id}`, txId: t.id, snapId: null,
                    kind: t.type === "transfer" ? "transfer-out" : t.type,
                    amount: t.amount, date: t.date, otherAccId: t.toAccountId, accountId: t.accountId, isSample: t.id < 0,
                  })),
                  ...effTrackTxs.filter(t => t.type === "transfer" && t.toAccountId === activeTrackAcc).map(t => ({
                    key: `tx-${t.id}-in`, txId: t.id, snapId: null,
                    kind: "transfer-in", amount: t.amount, date: t.date, otherAccId: t.accountId, accountId: t.toAccountId, isSample: t.id < 0,
                  })),
                  ...effTrackSnaps.filter(s => s.accountId === activeTrackAcc).map(s => ({
                    key: `snap-${s.id}`, txId: null, snapId: s.id,
                    kind: "snapshot", amount: s.value, date: s.date, otherAccId: null, accountId: s.accountId, isSample: s.id < 0,
                  })),
                ]
            ).filter(r => {
              if (!ledgerTypeFilter) return true;
              if (ledgerTypeFilter === "transfer") return r.kind === "transfer-out" || r.kind === "transfer-in";
              return r.kind === ledgerTypeFilter;
            }).sort((a, b) => {
              const dir = ledgerSort.dir === "asc" ? 1 : -1;
              if (ledgerSort.col === "amount") return dir * (a.amount - b.amount);
              return dir * a.date.localeCompare(b.date);
            });

            const depositBase = activeTrackAcc !== null
              ? getDeposits(activeTrackAcc)
              : trackAccounts.reduce((s, a) => s + getDeposits(a.id), 0);

            const totalDepositBase = activeTrackAcc !== null
              ? getDeposits(activeTrackAcc) - getTransfersOut(activeTrackAcc)
              : trackAccounts.reduce((s, a) => s + getDeposits(a.id) - getTransfersOut(a.id), 0);

            const fmtAbs = (v) => {
              if (v === null) return "—";
              const abs = Math.abs(v);
              return `${v >= 0 ? "+" : "-"}${currency}${abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
            };
            const fmtPct = (v, base = depositBase) => {
              if (v === null || !base) return null;
              const pct = (v / base) * 100;
              return `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%`;
            };
            const perfCol  = (v) => v === null ? "#ccc" : v >= 0 ? "#4a9d5f" : "#d94f4f";
            const otherAccName = (id) => trackAccounts.find(a => a.id === id)?.name ?? "";
            const typeLabel = { deposit: "Deposit", withdrawal: "Withdrawal", "transfer-out": "Transfer →", "transfer-in": "Transfer ←", snapshot: "Value" };
            const typeBg    = { deposit: "#edf7f0", withdrawal: "#fdf0f0", "transfer-out": "#fdf0f0", "transfer-in": "#edf7f0", snapshot: "#f5f5f5" };
            const typeCol   = { deposit: "#4a9d5f", withdrawal: "#d94f4f", "transfer-out": "#d94f4f", "transfer-in": "#4a9d5f", snapshot: "#888" };

            const metricCards = [
              { key: "total", label: "Total performance", value: totalPerfVal, base: totalDepositBase,
                tooltip: "This is your ultimate performance – not just the p/l of open positions. It is your current account value / the total of your deposits.\n\nWithdrawals / spend of your profits will reduce your total performance; transfers between accounts will not." },
              { key: "ghost", label: "Ghost performance", value: ghostPerfVal, base: totalDepositBase,
                tooltip: "This is the view of your all-time performance, had you have never spent any profits / withdrawn from the account.\n\nWithdrawals / spend of your profits will not reduce your total performance." },
            ];

            return (
              <div style={{ flex: 1, display: "flex", flexDirection: isMobile ? "column" : "row", overflow: isMobile ? "visible" : "hidden", minWidth: 0 }}>

                {/* LEFT: Inputs */}
                <div style={{ width: isMobile ? "100%" : 220, flexShrink: 0, borderRight: isMobile ? "none" : "0.5px solid rgba(0,0,0,0.07)", borderBottom: isMobile ? "0.5px solid rgba(0,0,0,0.07)" : "none", display: "flex", flexDirection: "column", overflowY: isMobile ? "visible" : "auto", padding: isMobile ? "20px 16px" : "32px 20px", gap: 28 }}>

                  {/* Account value snapshot */}
                  <AppTooltip
                    label="Periodically enter the account value for each of your investment accounts – get both an aggregated & individual account view"
                    enabled={tooltipsEnabled}
                    position="right"
                  >
                  <div>
                    <Text size="xs" c="#999" style={{ letterSpacing: "0.07em", textTransform: "uppercase", fontWeight: 600, marginBottom: 14 }}>Track performance</Text>
                    <select value={activeTrackAcc ?? trackAccounts[0]?.id ?? ""} onChange={e => setActiveTrackAcc(Number(e.target.value))}
                      style={{ width: "100%", padding: "7px 8px", fontSize: 13, border: "0.5px solid rgba(0,0,0,0.2)", borderRadius: 4, outline: "none", color: "#333", background: "white", boxSizing: "border-box", marginBottom: 12 }}>
                      {trackAccounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                    <div style={{ position: "relative", marginBottom: 10 }}>
                      <span style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "#777", pointerEvents: "none" }}>{currency}</span>
                      <input type="number" min="0" placeholder="0.00" value={snapAmount}
                        onChange={e => setSnapAmount(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && addTrackSnap()}
                        style={{ width: "100%", padding: "7px 8px 7px 22px", fontSize: 13, border: "0.5px solid rgba(0,0,0,0.2)", borderRadius: 4, outline: "none", boxSizing: "border-box", color: "#333", background: "white", MozAppearance: "textfield" }} />
                    </div>
                    <div style={{ marginBottom: 14 }}>
                      {snapEditDate
                        ? <input type="date" value={snapDate} onChange={e => setSnapDate(e.target.value)} onBlur={() => setSnapEditDate(false)} autoFocus
                            style={{ width: "100%", padding: "7px 8px", fontSize: 13, border: "0.5px solid rgba(0,0,0,0.2)", borderRadius: 4, outline: "none", boxSizing: "border-box", color: "#333", background: "white" }} />
                        : <button onClick={() => setSnapEditDate(true)}
                            style={{ width: "100%", padding: "7px 8px", fontSize: 13, border: "0.5px solid rgba(0,0,0,0.15)", borderRadius: 4, background: "white", color: "#555", textAlign: "left", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            {fmtDate(snapDate)} <span style={{ color: "#ccc", fontSize: 11 }}>✎</span>
                          </button>
                      }
                    </div>
                    {!subscribed && trackSnapCount >= SNAP_LIMIT
                      ? <Button variant="filled" size="sm" radius="xl" fullWidth onClick={openCheckout}>Subscribe</Button>
                      : <Button variant="filled" size="sm" radius="xl" fullWidth onClick={addTrackSnap}
                          disabled={snapAmount === "" || parseFloat(snapAmount) < 0}>
                          Save value
                        </Button>
                    }
                  </div>
                  </AppTooltip>

                  <div style={{ height: "0.5px", background: "rgba(0,0,0,0.07)" }} />

                  {/* Add entry */}
                  <AppTooltip
                    label="Track your deposits, withdrawals & transfers between accounts – this data forms the basis for your long-term performance metric"
                    enabled={tooltipsEnabled}
                    position="right"
                  >
                  <div>
                    <Text size="xs" c="#999" style={{ letterSpacing: "0.07em", textTransform: "uppercase", fontWeight: 600, marginBottom: 14 }}>Track transactions</Text>
                    <select value={activeTrackAcc ?? trackAccounts[0]?.id ?? ""} onChange={e => setActiveTrackAcc(Number(e.target.value))}
                      style={{ width: "100%", padding: "7px 8px", fontSize: 13, border: "0.5px solid rgba(0,0,0,0.2)", borderRadius: 4, outline: "none", color: "#333", background: "white", boxSizing: "border-box", marginBottom: 12 }}>
                      {trackAccounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                    <select value={txType} onChange={e => setTxType(e.target.value)}
                      style={{ width: "100%", padding: "7px 8px", fontSize: 13, border: "0.5px solid rgba(0,0,0,0.2)", borderRadius: 4, outline: "none", color: "#333", background: "white", boxSizing: "border-box", marginBottom: 12 }}>
                      <option value="deposit">Deposit</option>
                      <option value="withdrawal">Withdrawal</option>
                      <option value="transfer">Transfer</option>
                    </select>
                    <div style={{ position: "relative", marginBottom: 10 }}>
                      <span style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "#777", pointerEvents: "none" }}>{currency}</span>
                      <input type="number" min="0" placeholder="0.00" value={txAmount}
                        onChange={e => setTxAmount(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && addTrackTx()}
                        style={{ width: "100%", padding: "7px 8px 7px 22px", fontSize: 13, border: "0.5px solid rgba(0,0,0,0.2)", borderRadius: 4, outline: "none", boxSizing: "border-box", color: "#333", background: "white", MozAppearance: "textfield" }} />
                    </div>
                    {txType === "transfer" && (
                      <div style={{ marginBottom: 10 }}>
                        {trackAccounts.length < 2
                          ? <Text size="xs" c="#ccc" style={{ padding: "4px 0" }}>Add another account to transfer</Text>
                          : <select value={txToAcc ?? ""} onChange={e => setTxToAcc(Number(e.target.value))}
                              style={{ width: "100%", padding: "7px 8px", fontSize: 13, border: "0.5px solid rgba(0,0,0,0.2)", borderRadius: 4, outline: "none", color: "#333", background: "white", boxSizing: "border-box" }}>
                              <option value="">To account…</option>
                              {trackAccounts.filter(a => a.id !== (activeTrackAcc ?? trackAccounts[0]?.id)).map(a => (
                                <option key={a.id} value={a.id}>{a.name}</option>
                              ))}
                            </select>
                        }
                      </div>
                    )}
                    <div style={{ marginBottom: 14 }}>
                      {txEditDate
                        ? <input type="date" value={txDate} onChange={e => setTxDate(e.target.value)} onBlur={() => setTxEditDate(false)} autoFocus
                            style={{ width: "100%", padding: "7px 8px", fontSize: 13, border: "0.5px solid rgba(0,0,0,0.2)", borderRadius: 4, outline: "none", boxSizing: "border-box", color: "#333", background: "white" }} />
                        : <button onClick={() => setTxEditDate(true)}
                            style={{ width: "100%", padding: "7px 8px", fontSize: 13, border: "0.5px solid rgba(0,0,0,0.15)", borderRadius: 4, background: "white", color: "#555", textAlign: "left", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            {fmtDate(txDate)} <span style={{ color: "#ccc", fontSize: 11 }}>✎</span>
                          </button>
                      }
                    </div>
                    {!subscribed && trackTxCount >= TX_LIMIT
                      ? <Button variant="default" size="sm" radius="xl" fullWidth onClick={openCheckout}>Subscribe</Button>
                      : <Button variant="default" size="sm" radius="xl" fullWidth onClick={addTrackTx}
                          disabled={!txAmount || parseFloat(txAmount) <= 0 || (txType === "transfer" && (!txToAcc || trackAccounts.length < 2))}>
                          Add {txType}
                        </Button>
                    }
                  </div>
                  </AppTooltip>
                </div>

                {/* CENTRE + RIGHT wrapper — pills span both columns */}
                <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: isMobile ? "visible" : "hidden", minWidth: 0 }}>

                {/* TOP: Account pills */}
                <div style={{ flexShrink: 0, borderBottom: "0.5px solid rgba(0,0,0,0.07)", padding: "14px 16px", overflowX: "auto" }}>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", minWidth: "max-content" }}>
                    <button onClick={() => setActiveTrackAcc(null)} style={{
                      padding: "5px 12px", borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: "pointer",
                      background: activeTrackAcc === null ? "#003129" : "transparent",
                      color: activeTrackAcc === null ? "white" : "#003129",
                      border: `0.5px solid ${activeTrackAcc === null ? "#003129" : "#00665A"}`,
                    }}>All accounts</button>
                    {trackAccounts.map(acc => (
                      <div key={acc.id} style={{ display: "flex", alignItems: "center", gap: 2 }}>
                        {editingAccName === acc.id
                          ? <input value={editAccNameVal} autoFocus
                              onChange={e => setEditAccNameVal(e.target.value)}
                              onBlur={() => commitAccName(acc.id)}
                              onKeyDown={e => { if (e.key === "Enter") commitAccName(acc.id); if (e.key === "Escape") { setEditingAccName(null); setEditAccNameVal(""); } }}
                              style={{ padding: "4px 10px", borderRadius: 20, fontSize: 12, border: "1.5px solid #003129", outline: "none", width: 110 }} />
                          : <button
                              onClick={() => setActiveTrackAcc(acc.id)}
                              onDoubleClick={() => { setEditingAccName(acc.id); setEditAccNameVal(acc.name); }}
                              title="Double-click to rename"
                              style={{
                                padding: "5px 12px", borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: "pointer",
                                background: activeTrackAcc === acc.id ? "#003129" : "transparent",
                                color: activeTrackAcc === acc.id ? "white" : "#003129",
                                border: `0.5px solid ${activeTrackAcc === acc.id ? "#003129" : "#00665A"}`,
                              }}>
                              {acc.name}
                            </button>
                        }
                        {trackAccounts.length > 1 && (
                          <button onClick={() => setDeleteAccConfirm(acc.id)}
                            style={{ background: "none", border: "none", cursor: "pointer", color: "#ccc", fontSize: 15, padding: "2px 3px", lineHeight: 1 }}>×</button>
                        )}
                      </div>
                    ))}
                    {trackAccounts.length < 20 && (
                      <button onClick={addTrackAccount}
                        style={{ padding: "5px 12px", borderRadius: 20, fontSize: 12, color: "#aaa", background: "transparent", border: "0.5px dashed rgba(0,0,0,0.2)", cursor: "pointer" }}>
                        + Add account
                      </button>
                    )}
                  </div>
                </div>

                {/* CENTRE + RIGHT row */}
                <div style={{ flex: 1, display: "flex", flexDirection: isMobile ? "column" : "row", overflow: "hidden" }}>

                {/* CENTRE: Metric cards + Line chart */}
                <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: isMobile ? "visible" : "hidden", minWidth: 0 }}>

                  {/* Metric cards */}
                  <div style={{ flexShrink: 0, display: "flex", gap: isMobile ? 8 : 16, padding: isMobile ? "12px 12px" : "24px 28px", borderBottom: "0.5px solid rgba(0,0,0,0.07)" }}>
                    {metricCards.map(({ key, label, value, base, tooltip }) => (
                      <AppTooltip key={key} label={tooltip} enabled={tooltipsEnabled} position="bottom">
                        <div style={{
                          flex: 1, padding: "16px 18px", borderRadius: 6,
                          border: "0.5px solid rgba(0,0,0,0.1)",
                          background: "white",
                        }}>
                          <Text size="xs" c="#999" style={{ marginBottom: 8, letterSpacing: "0.03em" }}>{label}</Text>
                          <Text style={{ fontSize: isMobile ? 15 : 20, fontWeight: 600, color: perfCol(value) }}>
                            {fmtPct(value, base) ?? "—"}
                            {fmtPct(value, base) !== null && (
                              <span style={{ fontSize: 13, fontWeight: 400, marginLeft: 6, opacity: 0.65 }}>({fmtAbs(value)})</span>
                            )}
                          </Text>
                        </div>
                      </AppTooltip>
                    ))}
                  </div>

                  {/* Line chart */}
                  <div style={{ flex: isMobile ? "none" : 1, height: isMobile ? 240 : "auto", overflow: "hidden", padding: "20px 20px 20px 8px", position: "relative" }}>
                    {isSampleTrackActive && (
                      <div style={{ position: "absolute", top: "33.33%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 2, padding: "5px 14px", borderRadius: 12, background: "rgba(255,255,255,0.94)", boxShadow: "0 2px 12px rgba(0,0,0,0.1)", fontSize: 11, fontWeight: 600, letterSpacing: "0.04em", color: "#5a7d77", textTransform: "uppercase", whiteSpace: "nowrap" }}>
                        Sample data – hit '+ Add account' to capture & track your performance
                      </div>
                    )}
                    <div ref={chartContainerRef} style={{ position: "absolute", inset: "20px 20px 20px 8px" }}>
                    {chartData.length < 2
                      ? <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Text size="sm" c="#ddd">Add at least two account value snapshots to see performance over time</Text>
                        </div>
                      : (() => {
                          const VW = chartDims.w, VH = chartDims.h;
                          const P  = { t: 16, r: 16, b: 40, l: 60 };
                          const pw = VW - P.l - P.r, ph = VH - P.t - P.b;
                          const vals   = chartData.map(d => d.value);
                          const rawMin = Math.min(...vals), rawMax = Math.max(...vals);
                          const span   = rawMax - rawMin || 1;
                          const minV   = rawMin - span * 0.12, maxV = rawMax + span * 0.12;
                          const vSpan  = maxV - minV;
                          const sx = (i) => P.l + (chartData.length > 1 ? (i / (chartData.length - 1)) * pw : pw / 2);
                          const sy = (v) => P.t + ph - ((v - minV) / vSpan) * ph;
                          const col   = "#003129";
                          const roughStep = vSpan / 5;
                          const mag = Math.pow(10, Math.floor(Math.log10(roughStep)));
                          const niceStep = [1, 2, 2.5, 5, 10].map(x => x * mag).find(s => s >= roughStep) ?? mag * 10;
                          const tickStart = Math.ceil(minV / niceStep) * niceStep;
                          const yTicks = Array.from({ length: Math.ceil((maxV - tickStart) / niceStep) + 1 }, (_, i) => tickStart + i * niceStep).filter(t => t >= minV && t <= maxV);
                          const showEvery = chartData.length <= 6 ? 1 : Math.ceil(chartData.length / 6);
                          const fmtV = (v) => {
                            const a = Math.abs(v), s = v < 0 ? "-" : "";
                            if (a >= 10000) return `${s}${currency}${Math.round(a / 1000)}k`;
                            if (a >= 1000)  return `${s}${currency}${(a / 1000).toFixed(1)}k`;
                            return `${s}${currency}${Math.round(a)}`;
                          };
                          const fmtD = (iso) => {
                            const [,m,d] = iso.split("-");
                            return `${parseInt(d)} ${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][parseInt(m)-1]}`;
                          };
                          const areaFloor = minV;
                          const costBasis = activeTrackAcc !== null
                            ? getDeposits(activeTrackAcc)
                            : trackAccounts.reduce((s, a) => s + getDeposits(a.id), 0);
                          const showCostLine = costBasis >= minV && costBasis <= maxV;
                          const pts = chartData.map((d, i) => [sx(i), sy(d.value)]);
                          const tension = 0.18;
                          const smoothD = pts.reduce((d, p, i) => {
                            if (i === 0) return `M ${p[0]},${p[1]}`;
                            const p0 = pts[Math.max(i - 2, 0)];
                            const p1 = pts[i - 1];
                            const p2 = p;
                            const p3 = pts[Math.min(i + 1, pts.length - 1)];
                            const cp1x = p1[0] + (p2[0] - p0[0]) * tension;
                            const cp1y = p1[1] + (p2[1] - p0[1]) * tension;
                            const cp2x = p2[0] - (p3[0] - p1[0]) * tension;
                            const cp2y = p2[1] - (p3[1] - p1[1]) * tension;
                            return `${d} C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2[0]},${p2[1]}`;
                          }, "");
                          const areaD = `M ${pts[0][0]},${sy(areaFloor)} L ${smoothD.slice(2)} L ${pts[pts.length - 1][0]},${sy(areaFloor)} Z`;

                          const txsForChart = (() => {
                            if (activeTrackAcc !== null) {
                              return [
                                ...effTrackTxs.filter(t => t.accountId === activeTrackAcc),
                                ...effTrackTxs.filter(t => t.type === "transfer" && t.toAccountId === activeTrackAcc)
                                  .map(t => ({ ...t, _kind: "transfer-in" })),
                              ];
                            }
                            return effTrackTxs.filter(t => t.type !== "transfer");
                          })();
                          const nearestChartPoint = (date) => {
                            let bestIdx = 0, bestDiff = Infinity;
                            chartData.forEach((d, i) => {
                              const diff = Math.abs(new Date(d.date) - new Date(date));
                              if (diff < bestDiff) { bestDiff = diff; bestIdx = i; }
                            });
                            return { x: sx(bestIdx), y: sy(chartData[bestIdx].value), idx: bestIdx };
                          };
                          const visibleTxMarkers = txsForChart
                            .filter(t => t.date >= chartData[0].date && t.date <= chartData[chartData.length - 1].date)
                            .map(t => ({ ...t, kind: t._kind || t.type }));
                          const markerGroups = {};
                          visibleTxMarkers.forEach(t => {
                            const { x, y, idx } = nearestChartPoint(t.date);
                            if (!markerGroups[idx]) markerGroups[idx] = { x, y, txs: [] };
                            markerGroups[idx].txs.push(t);
                          });
                          const markerColor = { deposit: "#2f9e44", withdrawal: "#e03131", transfer: "#f08c00", "transfer-in": "#1971c2" };
                          const markerSign  = { deposit: "+", withdrawal: "−", transfer: "=", "transfer-in": "+" };
                          const markerLabel = { deposit: "Deposit", withdrawal: "Withdrawal", transfer: "Transfer out", "transfer-in": "Transfer in" };
                          const legendKinds = [...new Set(visibleTxMarkers.map(t => t.kind))];

                          return (
                            <svg viewBox={`0 0 ${VW} ${VH}`} width="100%" height="100%" style={{ display: "block" }}>
                              <defs>
                                <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor="#CAFFFB" stopOpacity="1" />
                                  <stop offset="100%" stopColor="#CAFFFB" stopOpacity="0" />
                                </linearGradient>
                              </defs>
                              {yTicks.map((v, i) => (
                                <g key={i}>
                                  <line x1={P.l} y1={sy(v)} x2={VW - P.r} y2={sy(v)} stroke="rgba(0,0,0,0.06)" strokeWidth={0.5} />
                                  <text x={P.l - 6} y={sy(v)} textAnchor="end" dominantBaseline="middle"
                                    style={{ fontSize: 14, fill: "#bbb", fontFamily: "Inter, system-ui, sans-serif" }}>{fmtV(v)}</text>
                                </g>
                              ))}
                              {showCostLine && (
                                <line x1={P.l} y1={sy(costBasis)} x2={VW - P.r} y2={sy(costBasis)} stroke="rgba(0,0,0,0.18)" strokeWidth={0.8} strokeDasharray="4,3" />
                              )}
                              <path d={areaD} fill="url(#areaGradient)" />
                              <path d={smoothD} fill="none" stroke={col} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                              {chartData.map((d, i) => (
                                <circle key={i} cx={sx(i)} cy={sy(d.value)} r={3} fill={col} />
                              ))}
                              {/* Event marker circles — snapped to nearest snapshot, above everything */}
                              {Object.values(markerGroups).map(({ x: baseX, y: lineY, txs }) =>
                                txs.map((t, idx) => {
                                  const cx = txs.length > 1 ? baseX + (idx - (txs.length - 1) / 2) * 26 : baseX;
                                  const color = markerColor[t.kind] ?? "#888";
                                  const tipText = `${currency}${Number(t.amount).toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
                                  return (
                                    <g key={`ev-${t.id}-${t.kind}`} style={{ cursor: "default" }}
                                      onMouseEnter={() => setEvMarkerHover({ cx, cy: lineY, text: tipText, label: markerLabel[t.kind], color })}
                                      onMouseLeave={() => setEvMarkerHover(null)}>
                                      <circle cx={cx} cy={lineY} r={11} fill={color} opacity={0.9} />
                                      <text x={cx} y={lineY} textAnchor="middle" dominantBaseline="central"
                                        style={{ fontSize: 14, fill: "white", fontFamily: "Inter, system-ui, sans-serif", fontWeight: 700, pointerEvents: "none", userSelect: "none" }}>
                                        {markerSign[t.kind]}
                                      </text>
                                    </g>
                                  );
                                })
                              )}
                              {chartData.map((d, i) => (
                                (i % showEvery === 0 || i === chartData.length - 1) && (
                                  <text key={i} x={sx(i)} y={VH - P.b + 14} textAnchor="middle"
                                    style={{ fontSize: 14, fill: "#bbb", fontFamily: "Inter, system-ui, sans-serif" }}>{fmtD(d.date)}</text>
                                )
                              ))}
                              <line x1={P.l} y1={P.t} x2={P.l} y2={P.t + ph} stroke="rgba(0,0,0,0.1)" strokeWidth={0.5} />
                              {/* Event legend — top-right corner */}
                              {legendKinds.map((k, i) => (
                                <g key={k}>
                                  <circle cx={VW - P.r - 5} cy={P.t + 6 + i * 16} r={4} fill={markerColor[k]} opacity={0.9} />
                                  <text x={VW - P.r - 13} y={P.t + 6 + i * 16} textAnchor="end" dominantBaseline="central"
                                    style={{ fontSize: 11, fill: "#bbb", fontFamily: "Inter, system-ui, sans-serif" }}>
                                    {markerLabel[k]}
                                  </text>
                                </g>
                              ))}
                              {/* Hover tooltip — rendered last so it's always on top */}
                              {evMarkerHover && (() => {
                                const tw = 120, th = 36, pad = 14;
                                const tx = Math.min(Math.max(evMarkerHover.cx - tw / 2, P.l), VW - P.r - tw);
                                const ty = evMarkerHover.cy - th - pad;
                                return (
                                  <g pointerEvents="none">
                                    <rect x={tx} y={ty} width={tw} height={th} rx={5} fill="rgba(20,20,20,0.82)" />
                                    <text x={tx + tw / 2} y={ty + 13} textAnchor="middle" dominantBaseline="central"
                                      style={{ fontSize: 11, fill: "#aaa", fontFamily: "Inter, system-ui, sans-serif" }}>
                                      {evMarkerHover.label}
                                    </text>
                                    <text x={tx + tw / 2} y={ty + 26} textAnchor="middle" dominantBaseline="central"
                                      style={{ fontSize: 13, fill: "white", fontFamily: "Inter, system-ui, sans-serif", fontWeight: 600 }}>
                                      {evMarkerHover.text}
                                    </text>
                                  </g>
                                );
                              })()}
                            </svg>
                          );
                        })()
                    }
                    </div>
                  </div>
                </div>

                {/* RIGHT: Ledger */}
                <div style={{ width: isMobile ? "100%" : 420, flexShrink: 0, borderLeft: isMobile ? "none" : "0.5px solid rgba(0,0,0,0.07)", borderTop: isMobile ? "0.5px solid rgba(0,0,0,0.07)" : "none", display: "flex", flexDirection: "column", overflow: "hidden" }}>

                  {/* Ledger */}
                  <div style={{ flex: 1, overflowY: "auto" }}>
                    <div style={{ padding: "40px 16px 16px", display: "flex", alignItems: "center", gap: 10 }}>
                      <Text size="sm" c="#444" style={{ letterSpacing: "0.06em", marginBottom: 24 }}>
                        Balance sheet
                      </Text>
                      {isSampleTrackActive && (
                        <Text size="xs" style={{ marginBottom: 24, padding: "2px 8px", borderRadius: 10, background: "rgba(0,49,41,0.06)", color: "#5a7d77", fontWeight: 600, letterSpacing: "0.03em" }}>
                          Sample
                        </Text>
                      )}
                    </div>
                    <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                      <thead>
                        <tr style={{ borderBottom: "0.5px solid rgba(0,0,0,0.07)", position: "sticky", top: 0, background: "white", zIndex: 1 }}>
                          <th onClick={() => setLedgerSort(prev => ({ col: "date", dir: prev.col === "date" && prev.dir === "desc" ? "asc" : "desc" }))}
                            style={{ padding: "10px 16px", textAlign: "left", color: "#bbb", fontWeight: 400, fontSize: 10, letterSpacing: "0.07em", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>
                            DATE {ledgerSort.col === "date" ? (ledgerSort.dir === "asc" ? "↑" : "↓") : ""}
                          </th>
                          <th style={{ padding: "10px 8px", textAlign: "left", color: "#bbb", fontWeight: 400, fontSize: 10, letterSpacing: "0.07em", cursor: "pointer", userSelect: "none", position: "relative" }}
                            onClick={() => setLedgerTypeFilterOpen(prev => !prev)}>
                            DETAILS {ledgerTypeFilter ? "●" : "▾"}
                            {ledgerTypeFilterOpen && (
                              <div onClick={e => e.stopPropagation()} style={{ position: "absolute", top: "100%", left: 0, background: "white", border: "0.5px solid rgba(0,0,0,0.15)", borderRadius: 4, boxShadow: "0 2px 8px rgba(0,0,0,0.08)", zIndex: 20, minWidth: 120 }}>
                                {[null, "deposit", "withdrawal", "transfer", "snapshot"].map(f => (
                                  <div key={f ?? "all"} onClick={() => { setLedgerTypeFilter(f); setLedgerTypeFilterOpen(false); }}
                                    style={{ padding: "7px 12px", cursor: "pointer", fontSize: 12, fontWeight: ledgerTypeFilter === f ? 600 : 400, color: ledgerTypeFilter === f ? "#1a1a1a" : "#777" }}>
                                    {f === null ? "All" : f === "deposit" ? "Deposit" : f === "withdrawal" ? "Withdrawal" : f === "transfer" ? "Transfer" : "Value"}
                                  </div>
                                ))}
                              </div>
                            )}
                          </th>
                          <th onClick={() => setLedgerSort(prev => ({ col: "amount", dir: prev.col === "amount" && prev.dir === "desc" ? "asc" : "desc" }))}
                            style={{ padding: "10px 16px", textAlign: "right", color: "#bbb", fontWeight: 400, fontSize: 10, letterSpacing: "0.07em", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>
                            {ledgerSort.col === "amount" ? (ledgerSort.dir === "asc" ? "↑" : "↓") : ""} AMOUNT
                          </th>
                          <th style={{ width: 28 }} />
                        </tr>
                      </thead>
                      <tbody>
                        {ledgerRows.length === 0
                          ? <tr><td colSpan={4} style={{ padding: "40px 16px", textAlign: "center", color: "#ddd", fontSize: 13 }}>No entries yet</td></tr>
                          : ledgerRows.map(row => (
                            <tr key={row.key} style={{ borderBottom: "0.5px solid rgba(0,0,0,0.04)" }}>

                              {/* DATE */}
                              <td style={{ padding: "9px 16px", color: "#777", fontSize: 11, whiteSpace: "nowrap", cursor: row.isSample ? "default" : "pointer" }}
                                onClick={() => { if (row.isSample) return; setEditingLedgerCell({ key: row.key, field: "date" }); setEditingLedgerVal(row.date); }}>
                                {editingLedgerCell?.key === row.key && editingLedgerCell?.field === "date"
                                  ? <input type="date" value={editingLedgerVal} autoFocus
                                      style={{ fontSize: 11, border: "none", outline: "none", background: "transparent", width: 100 }}
                                      onChange={e => setEditingLedgerVal(e.target.value)}
                                      onBlur={() => commitLedgerEdit(row, "date", editingLedgerVal)}
                                      onKeyDown={e => {
                                        if (e.key === "Enter") commitLedgerEdit(row, "date", editingLedgerVal);
                                        if (e.key === "Escape") { setEditingLedgerCell(null); setEditingLedgerVal(""); }
                                      }} />
                                  : fmtDate(row.date)
                                }
                              </td>

                              {/* DETAILS */}
                              <td style={{ padding: "9px 8px" }}>
                                <div style={{ display: "flex", flexDirection: "row", gap: 4, alignItems: "center", flexWrap: "wrap" }}>
                                  {/* Type pill */}
                                  {editingLedgerCell?.key === row.key && editingLedgerCell?.field === "type"
                                    ? <select value={editingLedgerVal} autoFocus
                                        style={{ fontSize: 11, border: "0.5px solid rgba(0,0,0,0.2)", borderRadius: 3, padding: "2px 4px" }}
                                        onChange={e => commitLedgerEdit(row, "type", e.target.value)}
                                        onBlur={() => { setEditingLedgerCell(null); setEditingLedgerVal(""); }}>
                                        <option value="deposit">Deposit</option>
                                        <option value="withdrawal">Withdrawal</option>
                                        <option value="transfer">Transfer</option>
                                      </select>
                                    : <span
                                        onClick={() => {
                                          if (row.isSample || row.kind === "snapshot" || row.kind === "transfer-in") return;
                                          const toTxType = { deposit: "deposit", withdrawal: "withdrawal", "transfer-out": "transfer" };
                                          setEditingLedgerCell({ key: row.key, field: "type" });
                                          setEditingLedgerVal(toTxType[row.kind] ?? row.kind);
                                        }}
                                        style={{ padding: "2px 8px", borderRadius: 3, fontSize: 11, fontWeight: 500, display: "inline-block",
                                          background: typeBg[row.kind], color: typeCol[row.kind],
                                          cursor: !row.isSample && row.kind !== "snapshot" && row.kind !== "transfer-in" ? "pointer" : "default" }}>
                                        {typeLabel[row.kind]}
                                        {(row.kind === "transfer-out" || row.kind === "transfer-in") && row.otherAccId && (
                                          <span style={{ fontWeight: 400, opacity: 0.7, marginLeft: 3 }}>{otherAccName(row.otherAccId)}</span>
                                        )}
                                      </span>
                                  }
                                  {/* Account pill — only in All accounts view */}
                                  {activeTrackAcc === null && (
                                    editingLedgerCell?.key === row.key && editingLedgerCell?.field === "account"
                                      ? <select value={editingLedgerVal} autoFocus
                                          style={{ fontSize: 11, border: "0.5px solid rgba(0,0,0,0.2)", borderRadius: 3, padding: "2px 4px" }}
                                          onChange={e => commitLedgerEdit(row, "account", e.target.value)}
                                          onBlur={() => { setEditingLedgerCell(null); setEditingLedgerVal(""); }}>
                                          {trackAccounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                        </select>
                                      : <span
                                          onClick={() => {
                                            if (row.isSample) return;
                                            setEditingLedgerCell({ key: row.key, field: "account" });
                                            setEditingLedgerVal(String(row.accountId));
                                          }}
                                          style={{ padding: "2px 8px", borderRadius: 3, fontSize: 11, fontWeight: 500, display: "inline-block",
                                            background: "#f0f0f0", color: "#666", cursor: row.isSample ? "default" : "pointer" }}>
                                          {trackAccounts.find(a => a.id === row.accountId)?.name ?? "—"}
                                        </span>
                                  )}
                                </div>
                              </td>

                              {/* AMOUNT */}
                              <td style={{ padding: "9px 16px", textAlign: "right", fontWeight: 500, color: row.kind === "snapshot" ? "#444" : typeCol[row.kind], cursor: row.isSample ? "default" : "pointer" }}
                                onClick={() => { if (row.isSample) return; setEditingLedgerCell({ key: row.key, field: "amount" }); setEditingLedgerVal(String(row.amount)); }}>
                                {editingLedgerCell?.key === row.key && editingLedgerCell?.field === "amount"
                                  ? <input type="number" value={editingLedgerVal} autoFocus min="0"
                                      style={{ fontSize: 11, border: "none", outline: "none", background: "transparent", width: 80, textAlign: "right" }}
                                      onChange={e => setEditingLedgerVal(e.target.value)}
                                      onBlur={() => commitLedgerEdit(row, "amount", editingLedgerVal)}
                                      onKeyDown={e => {
                                        if (e.key === "Enter") commitLedgerEdit(row, "amount", editingLedgerVal);
                                        if (e.key === "Escape") { setEditingLedgerCell(null); setEditingLedgerVal(""); }
                                      }} />
                                  : (row.kind === "snapshot"
                                      ? `${currency}${row.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                      : `${row.kind === "deposit" || row.kind === "transfer-in" ? "+" : "-"}${currency}${row.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                    )
                                }
                              </td>

                              <td style={{ padding: "0 6px" }}>
                                {!row.isSample && (
                                  <button onClick={() => setDeleteEntryConfirm(row)}
                                    style={{ background: "none", border: "none", cursor: "pointer", color: "#ddd", fontSize: 15, padding: "4px", lineHeight: 1 }}>×</button>
                                )}
                              </td>
                            </tr>
                          ))
                        }
                      </tbody>
                    </table>
                    </div>{/* end overflowX scroll wrapper */}
                  </div>
                </div>

                </div>{/* end CENTRE + RIGHT row */}
                </div>{/* end CENTRE + RIGHT wrapper */}

              </div>
            );
          })()}

        </main>
      </div>
    </div>

    {/* Auth modal */}
    {/* Checkout success banner */}
    {checkoutSuccess && (
      <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: "#003129", color: "white", padding: "12px 20px", borderRadius: 8, fontSize: 14, fontWeight: 500, zIndex: 2000, display: "flex", alignItems: "center", gap: 12, boxShadow: "0 4px 20px rgba(0,0,0,0.2)" }}>
        Subscription active — enjoy unlimited access!
        <button onClick={() => setCheckoutSuccess(false)} style={{ background: "none", border: "none", color: "white", cursor: "pointer", fontSize: 18, lineHeight: 1, opacity: 0.7 }}>×</button>
      </div>
    )}

    {authModalOpen && (
      <div onClick={() => { setAuthModalOpen(false); setAuthSent(false); setAuthEmail(""); setAuthError(null); }}
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
        <div onClick={e => e.stopPropagation()}
          style={{ width: Math.min(360, windowWidth - 32), background: "white", borderRadius: 10, padding: "32px 28px", boxShadow: "0 8px 40px rgba(0,0,0,0.12)" }}>
          {authSent ? (
            <>
              <Text style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a", marginBottom: 8 }}>Check your email</Text>
              <Text size="sm" c="#999" style={{ marginBottom: 24 }}>We sent a login link to <strong>{authEmail}</strong>. It expires in 15 minutes.</Text>
              <button onClick={() => { setAuthSent(false); setAuthEmail(""); }}
                style={{ fontSize: 13, color: "#999", background: "none", border: "none", padding: 0, cursor: "pointer" }}>
                Use a different email
              </button>
            </>
          ) : (
            <>
              <Text style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a", marginBottom: 4 }}>Log in to Meedo</Text>
              <Text size="sm" c="#999" style={{ marginBottom: 12 }}>Enter your email and we'll send you a login link — no password needed.</Text>
              <Text size="xs" style={{ marginBottom: 16, color: "#003129", fontWeight: 600, letterSpacing: "0.04em" }}>CHECK YOUR JUNK MAIL FOLDER FOR THE LOGIN LINK</Text>
              <input
                type="email"
                placeholder="you@example.com"
                value={authEmail}
                onChange={e => setAuthEmail(e.target.value)}
                onKeyDown={e => e.key === "Enter" && !authSending && authEmail && sendMagicLink()}
                autoFocus
                style={{ width: "100%", padding: "9px 12px", fontSize: 13, border: "0.5px solid rgba(0,0,0,0.2)", borderRadius: 6, outline: "none", boxSizing: "border-box", marginBottom: 10, color: "#1a1a1a", fontFamily: "inherit" }}
              />
              {authError && <Text size="xs" c="red" style={{ marginBottom: 10 }}>{authError}</Text>}
              <Button variant="filled" size="sm" radius="xl" fullWidth onClick={sendMagicLink}
                disabled={!authEmail || authSending} loading={authSending}>
                Send login link
              </Button>
            </>
          )}
        </div>
      </div>
    )}

    {/* Insight disclaimer confirmation modal */}
    <Modal
      opened={insightDisclaimerOpen}
      onClose={() => setInsightDisclaimerOpen(false)}
      title="Before you continue"
      centered
      radius="md"
      size="sm"
    >
      <Text size="sm" c="#555" mb="lg" style={{ lineHeight: 1.7 }}>
        The insights generated are for information purposes only and are not investment advice. Insights are AI generated and can be inaccurate - do your own research and double-check responses.
      </Text>
      <Checkbox
        label="Do not show again"
        checked={insightDisclaimerChecked}
        onChange={(e) => setInsightDisclaimerChecked(e.currentTarget.checked)}
        mb="xl"
      />
      <div style={{ display: "flex", gap: "var(--mantine-spacing-md)" }}>
        <Button variant="filled" radius="xl" onClick={handleInsightDisclaimerUnderstand}>I understand</Button>
        <Button variant="default" radius="xl" onClick={handleInsightDisclaimerDecline}>I do not understand</Button>
      </div>
    </Modal>

    {/* CSV import preview modal */}
    <Modal
      opened={csvImportPreview !== null}
      onClose={() => setCsvImportPreview(null)}
      title="Import this portfolio?"
      centered
      radius="md"
      size="sm"
    >
      <Text size="sm" c="#555" mb="md" style={{ lineHeight: 1.6 }}>
        We've read the following holdings and allocations from your file. This will replace your current chart.
      </Text>
      <div style={{ maxHeight: 280, overflowY: "auto", border: "0.5px solid rgba(0,0,0,0.1)", borderRadius: 8, marginBottom: 20 }}>
        {(csvImportPreview || []).map((h, i) => (
          <div key={i} style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "9px 14px", fontSize: 13, color: "#333",
            borderBottom: i < csvImportPreview.length - 1 ? "0.5px solid rgba(0,0,0,0.07)" : "none",
          }}>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginRight: 12 }}>{h.name}</span>
            <span style={{ flexShrink: 0, color: "#777" }}>{Math.round(h.pct * 10) / 10}%</span>
          </div>
        ))}
      </div>
      <div style={{ background: "rgba(245,159,0,0.08)", border: "0.5px solid rgba(245,159,0,0.3)", borderRadius: 8, padding: "10px 14px", marginBottom: 16 }}>
        <Text size="xs" style={{ color: "#f59f00", lineHeight: 1.5 }}>
          Please ensure no sensitive info appears within this preview, before importing this data
        </Text>
      </div>
      <div style={{ display: "flex", gap: "var(--mantine-spacing-md)" }}>
        <Button variant="filled" radius="xl" onClick={handleCsvImportConfirm}>Use this portfolio</Button>
        <Button variant="default" radius="xl" onClick={() => setCsvImportPreview(null)}>Cancel</Button>
      </div>
    </Modal>

    {/* CSV import error modal */}
    <Modal
      opened={csvImportError !== null}
      onClose={() => setCsvImportError(null)}
      title="Couldn't import that file"
      centered
      radius="md"
      size="sm"
    >
      <Text size="sm" c="#555" mb="lg" style={{ lineHeight: 1.6 }}>
        {csvImportError}
      </Text>
      <Button variant="filled" radius="xl" onClick={() => setCsvImportError(null)}>OK</Button>
    </Modal>

    {/* Delete account confirmation modal */}
    <Modal
      opened={deleteAccConfirm !== null}
      onClose={() => setDeleteAccConfirm(null)}
      title={`Delete ${trackAccounts.find(a => a.id === deleteAccConfirm)?.name ?? ""} account?`}
      centered
      radius="md"
      size="sm"
    >
      <Text size="sm" c="#555" mb="xl">
        If you delete an account, all associated transaction data will be lost.
      </Text>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <Button variant="default" radius="xl" onClick={() => setDeleteAccConfirm(null)}>Cancel</Button>
        <Button variant="filled" color="red" radius="xl" onClick={() => { deleteTrackAccount(deleteAccConfirm); setDeleteAccConfirm(null); }}>Delete account</Button>
      </div>
    </Modal>

    {/* Delete balance sheet entry confirmation modal */}
    <Modal
      opened={deleteEntryConfirm !== null}
      onClose={() => setDeleteEntryConfirm(null)}
      title="Delete balance sheet entry?"
      centered
      radius="md"
      size="sm"
    >
      <Text size="sm" c="#555" mb="xl">
        This can't be undone.
      </Text>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <Button variant="default" radius="xl" onClick={() => setDeleteEntryConfirm(null)}>Cancel</Button>
        <Button variant="filled" color="red" radius="xl" onClick={() => { deleteTrackEntry(deleteEntryConfirm); setDeleteEntryConfirm(null); }}>Delete entry</Button>
      </div>
    </Modal>

    {/* Feedback modal */}
    <Modal
      opened={feedbackOpen}
      onClose={() => { setFeedbackOpen(false); setFeedbackSent(false); setFeedbackText(""); setFeedbackError(null); }}
      title="Feedback & feature requests"
      centered
      radius="md"
      styles={{
        content: { maxHeight: 600, display: "flex", flexDirection: "column" },
        header:  { flexShrink: 0 },
        body:    { flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", padding: 0 },
      }}
    >
      {feedbackSent ? (
        <Text size="sm" c="#444" style={{ lineHeight: 1.7, padding: "0 20px 20px" }}>
          Thanks for your feedback — we really appreciate it.
        </Text>
      ) : (
        <>
          <div style={{ flex: 1, overflowY: "auto", padding: "0 20px" }}>
            <Text size="sm" c="#666" style={{ marginBottom: 16, lineHeight: 1.6 }}>
              Share your thoughts & ideas for improving the platform and requesting new features / functionality
            </Text>
            <Textarea
              placeholder="Your message..."
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              minRows={4}
              autosize
            />
          </div>
          <div style={{ flexShrink: 0, padding: "12px 20px 20px", borderTop: "0.5px solid rgba(0,0,0,0.08)" }}>
            {feedbackError && <Text size="xs" c="red" style={{ marginBottom: 8 }}>{feedbackError}</Text>}
            <Button
              fullWidth
              variant="filled"
              radius="xl"
              onClick={sendFeedback}
              loading={feedbackSending}
              disabled={!feedbackText.trim()}
            >
              Send feedback
            </Button>
          </div>
        </>
      )}
    </Modal>


{cookieBanner && (
  <div style={{
    position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999,
    background: '#003129', color: '#fff',
    padding: '16px 24px', display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
    boxShadow: '0 -2px 12px rgba(0,0,0,0.15)',
  }}>
    <p style={{ fontSize: 13, lineHeight: 1.5, margin: 0, flex: 1, minWidth: 200 }}>
      We use cookies to understand how people use Meedo. <a href="https://meedo.uk/cookie-policy" style={{ color: '#00DAC8' }}>Cookie policy</a>
    </p>
    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
      <button onClick={rejectCookies} style={{ fontSize: 13, fontWeight: 600, cursor: 'pointer', borderRadius: 20, padding: '8px 18px', border: '1.5px solid #00DAC8', background: 'transparent', color: '#00DAC8' }}>Reject non-essential</button>
      <button onClick={acceptCookies} style={{ fontSize: 13, fontWeight: 600, cursor: 'pointer', borderRadius: 20, padding: '8px 18px', border: '1.5px solid #00DAC8', background: '#00DAC8', color: '#003129' }}>Accept all</button>
    </div>
  </div>
)}

</MantineProvider>
  );
}